/**
 * VirtVMForm — Odoo-style full-screen detail view for Virtual Machines
 * Tabs: General / Cómputo / Discos / NICs / Delegación
 * SmartButtons: Hypervisor, Cluster, OS, Cómputo, Discos, NICs, Estado
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeftIcon,
  CheckIcon,
  ChevronDownIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
  TrashIcon,
  ComputerDesktopIcon,
  CpuChipIcon,
  ServerStackIcon,
  CircleStackIcon,
  SignalIcon,
  BuildingOfficeIcon,
  PlusIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import FormCardsShell from './FormCardsShell';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import useNavGuard from '../hooks/useNavGuard';
import useAgentUpdateStatus, { describeAgentUpdate } from '../hooks/useAgentUpdateStatus';
import { registroGuardado } from '../utils/savedRecord';
import {
  HOSTING_PROVIDERS,
  HOSTING_PROVIDER_OWN,
  hostingProviderLabel,
} from '../utils/hosting';
import DocSmartButton from './DocSmartButton';
import ContactsSmartButton from './ContactsSmartButton';
import CiActivityPanel from './common/CiActivityPanel';
import DeviceAccessPanel from './DeviceAccessPanel';
import VirtVMMonitoringPanel from './VirtVMMonitoringPanel';
import StorageVolumesPanel from './StorageVolumesPanel';
import SoftwareInventoryPanel from './SoftwareInventoryPanel';
import RemoteActions from './RemoteActions';
// La consola del agente es la misma que la de la ficha de una estación: una sola
// pantalla para encolar comandos, ver el log y lanzar la actualización.
import AgentActivityPanel from './AgentActivityPanel';
// AD del equipo (no del rol DC): el mismo panel que usa la ficha de una estación.
import WorkstationAdPanel from './WorkstationAdPanel';
import '../styles/DetailView.css';
import TabButton from './TabButton';
import DetailCrumb from './DetailCrumb';

const VM_TYPES = [
  { value: 'server',  label: 'Servidor' },
  { value: 'desktop', label: 'Escritorio' },
  { value: 'network', label: 'Red (router/FW)' },
  { value: 'storage', label: 'Almacenamiento' },
  { value: 'other',   label: 'Otro' },
];

const POWER_STATE = {
  on:        { bg: 'rgba(16,185,129,.15)',  color: '#6ee7b7', label: 'Encendida' },
  off:       { bg: 'rgba(239,68,68,.15)',   color: '#fca5a5', label: 'Apagada' },
  suspended: { bg: 'rgba(234,179,8,.15)',   color: '#fde68a', label: 'Suspendida' },
  unknown:   { bg: 'rgba(255,255,255,.06)', color: 'rgba(255,255,255,.4)', label: 'Desconocido' },
};

const AGENT_STATUS = {
  online:   { bg: 'rgba(16,185,129,.15)', color: '#6ee7b7', label: 'Agente online' },
  offline:  { bg: 'rgba(239,68,68,.15)', color: '#fca5a5', label: 'Agente offline' },
  'no-agent': { bg: 'rgba(245,158,11,.15)', color: '#fcd34d', label: 'Sin agente' },
  unknown:  { bg: 'rgba(255,255,255,.06)', color: 'rgba(255,255,255,.45)', label: 'Agente desconocido' },
};

const GUEST_AGENT = {
  ok: { label: 'Guest agent OK', color: '#16a34a' },
  outdated: { label: 'Guest agent desactualizado', color: '#d97706' },
  not_installed: { label: 'Guest agent no instalado', color: '#dc2626' },
  unknown: { label: 'Telemetría guest no disponible', color: '#6b7280' },
};

const GUEST_AGENT_HELP = {
  ok: 'Las guest tools responden correctamente dentro del SO invitado.',
  outdated: 'El agente guest responde, pero la versión detectada está desactualizada.',
  not_installed: 'No se han detectado guest tools instaladas en la VM.',
  unknown: 'El hypervisor no ha reportado estado de guest tools para esta VM.',
};

const DISK_FORMATS = ['thick', 'thin', 'raw', 'qcow2'];
const NIC_TYPES    = ['vmxnet3', 'e1000', 'virtio', 'rtl8139'];

const EMPTY_FORM = {
  name: '', vm_type: 'server', cpu_cores: '', ram_gb: '',
  os_name: '', os_version: '', hypervisor_id: '', cluster_id: '',
  // Quién proporciona la plataforma. Por defecto la nuestra; al elegir un
  // proveedor externo se sueltan hipervisor y clúster (no existen en Azure).
  hosting_provider: 'l6_ecosystem', hosting_region: '',
  power_state: 'off', uuid: '', is_template: false, ha_protected: false,
  delegated_company_id: '', notes: '', status: 'active',
  domain_name: null, part_of_domain: null,
  // Rol en Active Directory (lo consume la topología AD del servicio)
  ad_dc: false, ad_domain: '', ad_site: '', ad_global_catalog: false, ad_fsmo_roles: [],
  // Inventario que manda el agente instalado DENTRO de la máquina. Solo
  // lectura: lo escribe el agente, no el formulario.
  manufacturer: '', model: '', serial_number: '', ip_address: '', mac_address: '',
  cpu: '', os_release: '', os_build: '', bios_version: '', bios_release_date: '',
  os_install_date: '', license_status: '', license_channel: '', license_partial_key: '',
  license_oem_in_firmware: null, install_channel: '', install_version: '',
};

const FSMO_ROLE_OPTIONS = [
  ['schema_master', 'Schema Master'],
  ['domain_naming_master', 'Domain Naming Master'],
  ['pdc_emulator', 'PDC Emulator'],
  ['rid_master', 'RID Master'],
  ['infrastructure_master', 'Infrastructure Master'],
];
const EMPTY_DISK = { label: '', size_gb: '', disk_format: 'thin', datastore: '', bus_type: 'scsi', _new: true };
const EMPTY_NIC  = { label: '', mac_address: '', ip_address: '', port_group_name: '', nic_type: 'vmxnet3', _new: true };

function mapAgentDisks(detail) {
  const volumes = Array.isArray(detail?.logical_volumes) ? detail.logical_volumes : [];
  return volumes.map((vol, index) => ({
    id: `agent-vol-${index}`,
    label: vol.device || vol.mount_point || `Volumen ${index + 1}`,
    size_gb: vol.size_gb || '',
    disk_format: vol.filesystem || 'mounted',
    datastore: vol.origin || vol.mount_point || '—',
    bus_type: vol.usage || 'SO',
    _agent: true,
  }));
}

function mapAgentNics(detail) {
  const assignments = Array.isArray(detail?.ip_assignments) ? detail.ip_assignments : [];
  const nics = Array.isArray(detail?.agent_nics) ? detail.agent_nics : [];
  const ipsByInterface = new Map();

  assignments.forEach((entry) => {
    const key = entry.interface_ref || 'unbound';
    if (!ipsByInterface.has(key)) ipsByInterface.set(key, []);
    ipsByInterface.get(key).push(entry.ip);
  });

  return nics.map((nic, index) => {
    const ports = Array.isArray(nic.ports) ? nic.ports : [];
    const primaryPort = ports[0] || {};
    const portIps = ports.flatMap((port) => ipsByInterface.get(`nic-${port.id}`) || []);
    const mergedIps = Array.from(new Set(portIps)).filter(Boolean);

    return {
      id: nic.id || `agent-nic-${index}`,
      nic_label: nic.slot || primaryPort.label || `NIC ${index + 1}`,
      adapter_type: nic.model || 'detected',
      mac_address: primaryPort.mac_address || '',
      ip_address: mergedIps.join(', '),
      port_group_name: primaryPort.label || nic.manufacturer || 'Detectada por agente',
      vswitch_name: nic.model || '',
      _agent: true,
      ports,
    };
  });
}

function deriveNicName(nic, index) {
  const rawCandidates = [
    nic.nic_label,
    nic.port_group_name,
    nic.label,
    nic.slot,
  ].filter(Boolean);

  for (const candidate of rawCandidates) {
    const txt = String(candidate).trim();
    if (!txt) continue;
    const noSuffix = txt
      .replace(/\s+Microsoft\s+Hyper-V.*$/i, '')
      .replace(/\s+vmxnet3.*$/i, '')
      .replace(/\s+virtio.*$/i, '')
      .trim();
    if (noSuffix && !/^nic-[0-9a-f]{8,}$/i.test(noSuffix)) return noSuffix;
  }
  return `NIC ${index + 1}`;
}

// ── IP validation helpers ───────────────────────────────────────────────────────────────
function isValidIpv4(ip) {
  if (!ip || typeof ip !== 'string') return false;
  const parts = ip.trim().split('.');
  if (parts.length !== 4) return false;
  return parts.every(o => { const n = Number(o); return Number.isInteger(n) && n >= 0 && n <= 255 && o !== ''; });
}
function _ipv4ToInt(ip) {
  return ip.trim().split('.').reduce((acc, oct) => ((acc << 8) | parseInt(oct, 10)) >>> 0, 0);
}
function ipv4InSubnet(ip, cidr) {
  if (!cidr || !cidr.includes('/')) return true;
  try {
    const [netAddr, prefix] = cidr.split('/');
    const prefixLen = parseInt(prefix, 10);
    if (prefixLen === 0) return true;
    const mask = (~0 << (32 - prefixLen)) >>> 0;
    return (_ipv4ToInt(ip) & mask) === (_ipv4ToInt(netAddr) & mask);
  } catch { return true; }
}

export default function VirtVMForm({ vm, companyId, onClose, onSave, initialMode, variant = 'classic', navItems = [], navIndex = -1, onNavigate }) {
  const isCards = variant === 'cards';
  const isEditing = Boolean(vm?.id);
  const isOwnerView = !vm || String(vm.company_id) === String(companyId);
  /* Una VM delegada la ve la empresa cliente, pero pertenece al propietario:
     se abre SIEMPRE en lectura y no se ofrece «Editar». */
  const isDelegatedView = Boolean(
    vm && String(vm.company_id) !== String(companyId) && String(vm.delegated_company_id) === String(companyId)
  );
  /* La ficha se abre EN LECTURA (clic en la fila del listado); 'create' y 'edit'
     editan. Sin `initialMode` edita, como siempre. */
  const [editing, setEditing] = useState(
    isDelegatedView ? false : (initialMode ? initialMode !== 'view' : true)
  );

  const [form, setForm]     = useState(EMPTY_FORM);
  const [disks, setDisks]   = useState([]);
  const [nics, setNics]     = useState([]);
  
  const [syncingNow, setSyncingNow] = useState(false);
  const [syncQueued, setSyncQueued] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncStep, setSyncStep] = useState('idle');
  const [syncMode, setSyncMode] = useState('rescan');
  const [syncCommandId, setSyncCommandId] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [syncNotice, setSyncNotice] = useState(null);
  const [syncElapsed, setSyncElapsed] = useState(0);
  const [showSyncActionsMenu, setShowSyncActionsMenu] = useState(false);
  const syncPollRef = useRef(null);
  const syncTimedOutRef = useRef(false);
  const syncSoftTimeoutRef = useRef(null);
  const syncHardTimeoutRef = useRef(null);
  const syncElapsedRef = useRef(null);
  const syncActionsMenuRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Cambios sin guardar al pasar de ficha o cerrar. `handleSave` se define más
  // abajo: la función se evalúa al pulsar, no al montar.
  const navGuard = useNavGuard({
    dirty: isDirty,
    saving,
    entity: 'esta máquina virtual',
    actionLabel: 'pasas a otra máquina o cierras la ficha',
    save: () => handleSave(),
  });

  const [volumeCount, setVolumeCount] = useState(0);
  const [companyLuns, setCompanyLuns] = useState([]);
  const [error, setError]   = useState(null);
  const [tab, setTab]       = useState(isCards ? 'resumen' : 'general');
  const [accessCount, setAccessCount] = useState(0);
  const [agentInventory, setAgentInventory] = useState({
    ip_assignments: [],
    windows_roles: [],
    installed_software: [],
    services: [],
    current_sessions: [],
    local_users: [],
  });
  const [agentBackedDisks, setAgentBackedDisks] = useState(false);
  const [agentBackedNics, setAgentBackedNics] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  // Bloque `ad_client` que manda el agente: DN/OU, grupos, GPO, DC y BitLocker.
  const [agentAdClient, setAgentAdClient] = useState(null);
  // Última versión publicada del agente: la ficha la usa para la pill
  // «→ vX.Y.Z» cuando el equipo va por detrás, igual que en Equipos.
  const [latestAgentVersion, setLatestAgentVersion] = useState(null);
  const [softwareSearch, setSoftwareSearch] = useState('');
  const [servicesSearch, setServicesSearch] = useState('');
  const [softwareSort, setSoftwareSort] = useState({ key: 'name', dir: 'asc' });
  const [servicesSort, setServicesSort] = useState({ key: 'display_name', dir: 'asc' });
  const [agentTelemetry, setAgentTelemetry] = useState({
    agent_status: 'unknown',
    guest_agent: null,
    last_seen_at: null,
    agent_id: null,
    agent_version: null,
  });

  // Última actualización remota del agente: el estado llega del backend, que
  // solo marca «actualizado» cuando el latido reporta la versión objetivo.
  const {
    status: agentUpdateStatus,
    markEnqueued: markAgentUpdateEnqueued,
  } = useAgentUpdateStatus(companyId, {
    ciType: 'virt_vm',
    ciId: vm?.id,
    agentId: agentTelemetry.agent_id,
    enabled: Boolean(vm?.id && agentTelemetry.agent_id),
  });
  const agentUpdate = describeAgentUpdate(agentUpdateStatus);

  // Support lists
  const [hypervisors, setHypervisors] = useState([]);
  const [clusters, setClusters]       = useState([]);
  const [companies, setCompanies]     = useState([]);
  const [portGroups, setPortGroups]   = useState([]);   // all PGs for this company
  const [ipamSubnets, setIpamSubnets] = useState([]);   // active IPAM subnets

  // NIC add/edit form
  const [nicDraft, setNicDraft]               = useState(null);  // null = closed
  const [nicSubnet, setNicSubnet]             = useState('');
  const [nicNextIp, setNicNextIp]             = useState('');
  const [nicNextIpLoading, setNicNextIpLoading] = useState(false);
  const [nicIpValidation, setNicIpValidation] = useState({ format: null, inSubnet: null, occupied: null, checking: false });
  const _nicOccupancyTimer = useRef(null);
  const [nicIpMode, setNicIpMode]         = useState('auto');  // 'auto' | 'manual' | 'map'
  const [nicMapData, setNicMapData]       = useState(null);
  const [nicMapLoading, setNicMapLoading] = useState(false);
  const [nicBeingEdited, setNicBeingEdited] = useState(null); // { nic, idx } when editing existing
  const [reloadKey, setReloadKey] = useState(0);

  // Cancelar edición: recarga el estado guardado y sale del modo edición.
  const handleCancelEdit = () => {
    setReloadKey(n => n + 1);
    setEditing(false);
  };

  // ── Bootstrap ──
  useEffect(() => {
    if (vm) {
      setForm({
        name:                 vm.name || '',
        vm_type:              vm.vm_type || 'server',
        cpu_cores:            vm.cpu_cores ?? '',
        ram_gb:               vm.ram_gb ?? '',
        os_name:              vm.os_name || '',
        os_version:           vm.os_version || '',
        hypervisor_id:        vm.hypervisor_id ?? '',
        cluster_id:           vm.cluster_id ?? '',
        hosting_provider:     vm.hosting_provider ?? '',
        hosting_region:       vm.hosting_region ?? '',
        power_state:          vm.power_state || 'off',
        uuid:                 vm.uuid || '',
        is_template:          !!vm.is_template,
        ha_protected:         !!vm.ha_protected,
        delegated_company_id: vm.delegated_company_id ?? '',
        notes:                vm.notes || '',
        status:               vm.status || 'active',
        domain_name:          vm.domain_name ?? null,
        part_of_domain:       vm.part_of_domain ?? null,
        ad_dc:                !!vm.ad_dc,
        ad_domain:            vm.ad_domain || '',
        ad_site:              vm.ad_site || '',
        ad_global_catalog:    !!vm.ad_global_catalog,
        ad_fsmo_roles:        Array.isArray(vm.ad_fsmo_roles) ? vm.ad_fsmo_roles : [],
        // Inventario del agente (solo lectura)
        manufacturer:         vm.manufacturer || '',
        model:                vm.model || '',
        serial_number:        vm.serial_number || '',
        ip_address:           vm.ip_address || '',
        mac_address:          vm.mac_address || '',
        cpu:                  vm.cpu || '',
        os_release:           vm.os_release || '',
        os_build:             vm.os_build || '',
        bios_version:         vm.bios_version || '',
        bios_release_date:    vm.bios_release_date || '',
        os_install_date:      vm.os_install_date || '',
        license_status:       vm.license_status || '',
        license_channel:      vm.license_channel || '',
        license_partial_key:  vm.license_partial_key || '',
        license_oem_in_firmware: vm.license_oem_in_firmware ?? null,
        install_channel:      vm.install_channel || '',
        install_version:      vm.install_version || '',
      });
      setAgentTelemetry({
        agent_status: vm.agent_status || 'unknown',
        guest_agent: vm.guest_agent || null,
        last_seen_at: vm.last_seen_at || null,
        agent_id: vm.agent_id || null,
        agent_version: vm.agent_version || null,
      });
      // Load full detail (disks/nics)
      api.getVM(vm.id, companyId)
        .then(r => {
          const d = r.data || {};
          const derivedDisks = Array.isArray(d.logical_volumes) && d.logical_volumes.length > 0
            ? mapAgentDisks(d)
            : (d.disks || []);
          const derivedNics = Array.isArray(d.agent_nics) && d.agent_nics.length > 0
            ? mapAgentNics(d)
            : (d.nics || []);
          setDisks(derivedDisks);
          setNics(derivedNics);
          setAgentBackedDisks(Array.isArray(d.logical_volumes) && d.logical_volumes.length > 0);
          setAgentBackedNics(Array.isArray(d.agent_nics) && d.agent_nics.length > 0);
          setAgentInventory({
            ip_assignments: Array.isArray(d.ip_assignments) ? d.ip_assignments : [],
            windows_roles: Array.isArray(d.windows_roles) ? d.windows_roles : [],
            installed_software: Array.isArray(d.installed_software) ? d.installed_software : [],
            services: Array.isArray(d.services) ? d.services : [],
            current_sessions: Array.isArray(d.current_sessions) ? d.current_sessions : [],
            local_users: Array.isArray(d.local_users) ? d.local_users : [],
          });
          setAgentAdClient(d.ad_client || null);
          setLatestAgentVersion(d.latest_agent_version || null);
          setAgentTelemetry({
            agent_status: d.agent_status || 'unknown',
            guest_agent: d.guest_agent || null,
            last_seen_at: d.last_seen_at || null,
            agent_id: d.agent_id || null,
            agent_version: d.agent_version || null,
          });
        })
        .catch(() => {});
    } else {
      setForm(EMPTY_FORM);
      setDisks([]);
      setNics([]);
      setAgentInventory({
        ip_assignments: [],
        windows_roles: [],
        installed_software: [],
        services: [],
        current_sessions: [],
        local_users: [],
      });
      setAgentBackedDisks(false);
      setAgentBackedNics(false);
      setAgentTelemetry({
        agent_status: 'unknown',
        guest_agent: null,
        last_seen_at: null,
        agent_id: null,
        agent_version: null,
      });
    }
  }, [vm, companyId, reloadKey]);

  useEffect(() => {
    if (!companyId) return;
    Promise.all([
      api.getHypervisors(companyId),
      api.getClusters(companyId),
      api.getAuthorizedCompanies(),
      api.getPortGroups(companyId),
      api.getIpamSubnets(companyId),
    ]).then(([h, c, cos, pgs, subs]) => {
      setHypervisors(h.data || []);
      setClusters(c.data || []);
      setCompanies(Array.isArray(cos) ? cos : (cos.companies || []));
      setPortGroups(pgs.data || []);
      setIpamSubnets((subs.data || []).filter(s => s.status === 'active'));
    }).catch(() => {});
  }, [companyId]);

  // Unidades de la VM + catálogo de LUNs: permite colgar cada volumen de su LUN
  // de cabina (y que el diseñador dibuje la cadena equipo → volumen → LUN).
  const refreshVolumes = useCallback(async () => {
    if (!isEditing || !vm?.id || !companyId) return;
    try {
      const [luns, vols] = await Promise.all([
        api.getLuns(companyId),
        api.getVolumes(companyId, 'virt_vm', vm.id),
      ]);
      setCompanyLuns(luns.data || []);
      setVolumeCount((vols.data || []).length);
    } catch (_) { /* ignore */ }
  }, [isEditing, vm?.id, companyId]);

  useEffect(() => { refreshVolumes(); }, [refreshVolumes]);

  useEffect(() => {
    if (tab === 'deleg' && isDelegatedView) {
      setTab('general');
    }
  }, [tab, isDelegatedView]);

  const sf = (k, v) => { setForm(f => ({ ...f, [k]: v })); setIsDirty(true); };

  /* Cambiar a una plataforma externa suelta hipervisor y clúster: una máquina
     en Azure no corre en un hipervisor nuestro, y guardar las dos cosas deja
     datos que se contradicen. */
  const setHostingProvider = (value) => {
    setForm(f => {
      const next = { ...f, hosting_provider: value };
      if (value && value !== HOSTING_PROVIDER_OWN) {
        next.hypervisor_id = '';
        next.cluster_id = '';
      }
      return next;
    });
    setIsDirty(true);
  };

  // ── NIC draft helpers ──
  const EMPTY_NIC_DRAFT = { nic_label: '', mac_address: '', adapter_type: 'vmxnet3', port_group_id: '', ip_address: '' };

  const openAddNic = () => {
    setNicBeingEdited(null);
    setNicDraft({ ...EMPTY_NIC_DRAFT });
    setNicSubnet('');
    setNicNextIp('');
    setNicIpMode('auto');
    setNicMapData(null);
    setNicIpValidation({ format: null, inSubnet: null, occupied: null, checking: false });
  };

  const openEditNic = (nic, idx) => {
    const subnetId = nic.subnet_id ? String(nic.subnet_id) : '';
    const mode = nic.ip_address ? 'manual' : 'auto';
    setNicBeingEdited({ nic, idx });
    setNics(ns => ns.filter((_, i) => i !== idx));
    setNicDraft({
      nic_label:    nic.nic_label || nic.label || '',
      mac_address:  nic.mac_address || '',
      adapter_type: nic.adapter_type || nic.nic_type || 'vmxnet3',
      port_group_id: nic.port_group_id ? String(nic.port_group_id) : '',
      ip_address:   nic.ip_address || '',
    });
    setNicSubnet(subnetId);
    setNicIpMode(mode);
    setNicNextIp('');
    setNicMapData(null);
    setNicIpValidation({ format: null, inSubnet: null, occupied: null, checking: false });
  };

  // Real-time: format + subnet membership
  useEffect(() => {
    if (!nicDraft) { setNicIpValidation({ format: null, inSubnet: null, occupied: null, checking: false }); return; }
    const ip = (nicDraft.ip_address || '').trim();
    if (!ip) { setNicIpValidation(v => ({ ...v, format: null, inSubnet: null })); return; }
    const formatOk = isValidIpv4(ip);
    let inSubnet = null;
    if (formatOk && nicSubnet) {
      const sub = ipamSubnets.find(s => String(s.id) === String(nicSubnet));
      if (sub?.cidr) inSubnet = ipv4InSubnet(ip, sub.cidr);
    }
    setNicIpValidation(v => ({ ...v, format: formatOk, inSubnet }));
  }, [nicDraft?.ip_address, nicSubnet, ipamSubnets]); // eslint-disable-line

  // Debounced: occupancy check via IPAM API
  useEffect(() => {
    if (!nicDraft) return;
    const ip = (nicDraft.ip_address || '').trim();
    if (!ip || !isValidIpv4(ip)) {
      setNicIpValidation(v => ({ ...v, occupied: null, checking: false }));
      return;
    }
    setNicIpValidation(v => ({ ...v, checking: true, occupied: null }));
    const timer = setTimeout(async () => {
      try {
        const params = nicSubnet ? { q: ip, subnet_id: nicSubnet } : { q: ip };
        const res = await api.getIpamIPs(companyId, params);
        const found = (res.data || []).find(x => x.ip === ip);
        setNicIpValidation(v => ({ ...v, occupied: found ? found.status : null, checking: false }));
      } catch {
        setNicIpValidation(v => ({ ...v, occupied: null, checking: false }));
      }
    }, 700);
    return () => clearTimeout(timer);
  }, [nicDraft?.ip_address, nicSubnet, companyId]); // eslint-disable-line

  // Auto mode: fetch next free IP automatically when subnet changes
  useEffect(() => {
    if (nicIpMode !== 'auto' || !nicSubnet || !nicDraft) return;
    setNicNextIpLoading(true);
    api.getIpamSubnetNextIp(nicSubnet, companyId, 1)
      .then(r => {
        const suggested = (r.data?.ips || [])[0] || '';
        setNicNextIp(suggested);
        setNicDraft(d => d ? { ...d, ip_address: suggested } : d);
      })
      .catch(() => { setNicNextIp(''); })
      .finally(() => setNicNextIpLoading(false));
  }, [nicSubnet, nicIpMode]); // eslint-disable-line

  // Map mode: load subnet IP map
  useEffect(() => {
    if (nicIpMode !== 'map' || !nicSubnet) { setNicMapData(null); setNicMapLoading(false); return; }
    let cancelled = false;
    setNicMapLoading(true);
    setNicMapData(null);
    api.getIpamSubnetMap(nicSubnet, companyId, 512)
      .then(res => { if (!cancelled) setNicMapData(res.data || null); })
      .catch(() => { if (!cancelled) setNicMapData(null); })
      .finally(() => { if (!cancelled) setNicMapLoading(false); });
    return () => { cancelled = true; };
  }, [nicSubnet, nicIpMode, companyId]); // eslint-disable-line

  const autoFillIp = async () => {
    if (!nicSubnet) return;
    setNicNextIpLoading(true);
    try {
      const r = await api.getIpamSubnetNextIp(nicSubnet, companyId, 1);
      const suggested = (r.data?.ips || [])[0] || '';
      setNicNextIp(suggested);
      setNicDraft(d => ({ ...d, ip_address: suggested }));
    } catch { setNicNextIp(''); }
    finally { setNicNextIpLoading(false); }
  };

  const confirmAddNic = () => {
    if (!nicDraft?.nic_label?.trim()) return;
    // In manual mode only: validate IP format/range/occupancy
    if (nicIpMode === 'manual') {
      const v = nicIpValidation;
      if (v.format === false || v.inSubnet === false) return;
      if (v.occupied && v.occupied !== 'free' && v.occupied !== 'reserved') return;
      if (v.checking) return;
    }
    const pg = portGroups.find(p => p.id === Number(nicDraft.port_group_id));
    const newNic = {
      ...nicDraft,
      _new: true,
      // If editing a saved NIC, track original id for deletion on save
      _deleteId: (nicBeingEdited && !nicBeingEdited.nic._new && nicBeingEdited.nic.id) ? nicBeingEdited.nic.id : null,
      subnet_id: nicSubnet || null,
      port_group_name: pg?.name || '',
      vlan_id: pg?.vlan_id,
      vswitch_name: pg?.vswitch_name || '',
    };
    if (nicBeingEdited) {
      // Re-insert at original index
      setNics(ns => { const copy = [...ns]; copy.splice(nicBeingEdited.idx, 0, newNic); return copy; });
    } else {
      setNics(ns => [...ns, newNic]);
    }
    setNicBeingEdited(null);
    setNicDraft(null);
    setNicSubnet('');
    setNicNextIp('');
    setNicIpMode('auto');
    setNicMapData(null);
    setNicIpValidation({ format: null, inSubnet: null, occupied: null, checking: false });
  };

  const cancelNicForm = () => {
    if (nicBeingEdited) {
      // Restore original NIC at its original index
      setNics(ns => { const copy = [...ns]; copy.splice(nicBeingEdited.idx, 0, nicBeingEdited.nic); return copy; });
      setNicBeingEdited(null);
    }
    setNicDraft(null);
    setNicSubnet('');
    setNicNextIp('');
    setNicIpMode('auto');
    setNicMapData(null);
    setNicIpValidation({ format: null, inSubnet: null, occupied: null, checking: false });
  };

  // ── Save ──
  
  const closeSyncModal = () => {
    setShowSyncModal(false);
    setSyncStep('idle');
    setSyncMode('rescan');
    setSyncCommandId(null);
    setSyncNotice(null);
    syncTimedOutRef.current = false;
    if (syncPollRef.current) { clearInterval(syncPollRef.current); syncPollRef.current = null; }
    if (syncSoftTimeoutRef.current) { clearTimeout(syncSoftTimeoutRef.current); syncSoftTimeoutRef.current = null; }
    if (syncHardTimeoutRef.current) { clearTimeout(syncHardTimeoutRef.current); syncHardTimeoutRef.current = null; }
  };

  const handleSyncMenuAction = (action) => {
    setShowSyncActionsMenu(false);
    handleSyncNow(action);
  };

  const handleSyncNow = async (mode = 'rescan') => {
    if (!vm?.id || !agentTelemetry?.agent_id || !companyId) return;

    setSyncMode(mode);
    setShowSyncModal(true);
    setSyncStep('enqueueing');
    setSyncResult(null);
    setSyncError(null);
    setSyncNotice(null);
    setSyncCommandId(null);
    syncTimedOutRef.current = false;

    if (syncSoftTimeoutRef.current) {
      clearTimeout(syncSoftTimeoutRef.current);
      syncSoftTimeoutRef.current = null;
    }
    if (syncHardTimeoutRef.current) {
      clearTimeout(syncHardTimeoutRef.current);
      syncHardTimeoutRef.current = null;
    }

    try {
      const command = mode;
      const timeoutProfile = { softMs: 180000, hardMs: 480000 };
      const requestedFrom = 'sync_now_button';
      const payload = {
        source: 'server_form',
        requested_from: requestedFrom,
      };

      const res = await api.enqueueAgentCommand(companyId, {
        agent_id: agentTelemetry.agent_id,
        command,
        ci_type: 'virt_vm',
        ci_id: vm.id,
        payload,
      });

      const cmdId = res.command?.id;
      if (!cmdId) {
        setSyncStep('error');
        setSyncError('No se recibió ID de comando');
        return;
      }

      setSyncCommandId(cmdId);
      setSyncStep('pending');
      const pollStartedAt = Date.now();

      if (syncPollRef.current) clearInterval(syncPollRef.current);
      syncPollRef.current = setInterval(async () => {
        if (syncTimedOutRef.current) return;
        try {
          const poll = await api.getAgentCommandStatus(companyId, cmdId);
          if (syncTimedOutRef.current) return;
          if (!poll?.command) {
            throw new Error('Respuesta invalida del backend: falta command');
          }
          const st = poll.command?.status;

          if (st === 'running') setSyncStep('running');
          if (st === 'received') setSyncStep('pending');
          if (st === 'done') {
            setSyncStep('done');
            setSyncResult(poll.command?.result);
            setSyncNotice(null);
            
            clearInterval(syncPollRef.current);
            syncPollRef.current = null;
            if (syncSoftTimeoutRef.current) {
              clearTimeout(syncSoftTimeoutRef.current);
              syncSoftTimeoutRef.current = null;
            }
            if (syncHardTimeoutRef.current) {
              clearTimeout(syncHardTimeoutRef.current);
              syncHardTimeoutRef.current = null;
            }
          }
          if (st === 'error') {
            setSyncStep('error');
            setSyncError(poll.command?.result?.stderr || 'El agente reportó un error');
            setSyncNotice(null);
            
            clearInterval(syncPollRef.current);
            syncPollRef.current = null;
            if (syncSoftTimeoutRef.current) {
              clearTimeout(syncSoftTimeoutRef.current);
              syncSoftTimeoutRef.current = null;
            }
            if (syncHardTimeoutRef.current) {
              clearTimeout(syncHardTimeoutRef.current);
              syncHardTimeoutRef.current = null;
            }
          }
        } catch (pollError) {
          console.warn('[AGENT_UI:virt_vm_form] poll_failed', pollError);
        }
      }, 2000);

      syncSoftTimeoutRef.current = setTimeout(() => {
        if (syncPollRef.current && (syncStep === 'pending' || syncStep === 'running')) {
          setSyncNotice('Aviso: la sincronización está tardando más de lo habitual. Seguimos esperando resultado del agente.');
        }
      }, timeoutProfile.softMs);

      syncHardTimeoutRef.current = setTimeout(() => {
        if (syncPollRef.current) {
          syncTimedOutRef.current = true;
          clearInterval(syncPollRef.current);
          syncPollRef.current = null;
          setSyncStep(prev => {
            if (prev === 'pending') {
              setSyncNotice(null);
              setSyncError('Timeout: el agente no recogió el comando en 8 min. Verifica conectividad.');
              return 'error';
            }
            if (prev === 'running') {
              setSyncNotice(null);
              setSyncResult({ message: 'El agente sigue procesando en segundo plano.' });
              return 'done';
            }
            return prev;
          });
        }
      }, timeoutProfile.hardMs);
    } catch (e) {
      setSyncStep('error');
      setSyncError(e.message || 'No se pudo encolar la sincronización');
      setSyncNotice(null);
    }
  };

  useEffect(() => {
    if (syncStep === 'pending' || syncStep === 'running') {
      setSyncElapsed(0);
      syncElapsedRef.current = setInterval(() => setSyncElapsed((s) => s + 1), 1000);
    } else {
      if (syncElapsedRef.current) {
        clearInterval(syncElapsedRef.current);
        syncElapsedRef.current = null;
      }
    }
    return () => {
      if (syncElapsedRef.current) clearInterval(syncElapsedRef.current);
    };
  }, [syncStep]);

  useEffect(() => {
    const handleGlobalClick = (event) => {
      if (!showSyncActionsMenu) return;
      if (!syncActionsMenuRef.current?.contains(event.target)) {
        setShowSyncActionsMenu(false);
      }
    };
    document.addEventListener('mousedown', handleGlobalClick);
    return () => document.removeEventListener('mousedown', handleGlobalClick);
  }, [showSyncActionsMenu]);

const handleSave = async () => {
    if (isDelegatedView) {
      setError('Máquina delegada: solo la empresa propietaria puede editarla.');
      return;
    }
    if (!form.name.trim()) { setError('El nombre es obligatorio'); return; }
    try {
      setSaving(true); setError(null);
      const payload = {
        ...form,
        cpu_cores:            form.cpu_cores !== '' ? Number(form.cpu_cores) : null,
        ram_gb:               form.ram_gb !== '' ? Number(form.ram_gb) : null,
        hypervisor_id:        form.hypervisor_id !== '' ? Number(form.hypervisor_id) : null,
        cluster_id:           form.cluster_id !== '' ? Number(form.cluster_id) : null,
      };
      if (isOwnerView) {
        payload.delegated_company_id = form.delegated_company_id !== '' ? Number(form.delegated_company_id) : null;
      }
      let creado = null;
      let vmId;
      if (isEditing) {
        await api.updateVM(vm.id, payload, companyId);
        vmId = vm.id;
      } else {
        const r = await api.createVM(payload, companyId);
        creado = registroGuardado(r);
        vmId = r.data?.id ?? creado?.id;
      }
      if (vmId) {
        for (const d of disks.filter(d => d._new)) {
          await api.addVMDisk(vmId, { label: d.label, size_gb: Number(d.size_gb), disk_format: d.disk_format, datastore: d.datastore, bus_type: d.bus_type }, companyId);
        }
        for (const n of nics.filter(n => n._new)) {
          // If this NIC replaces an edited saved NIC, delete the old one first
          if (n._deleteId) {
            await api.deleteVMNic(vmId, n._deleteId, companyId);
          }
          await api.addVMNic(vmId, {
            nic_label:      n.nic_label,
            adapter_type:   n.adapter_type,
            mac_address:    n.mac_address || null,
            ip_address:     n.ip_address  || null,
            port_group_id:  n.port_group_id ? Number(n.port_group_id) : null,
            subnet_id:      n.subnet_id ? Number(n.subnet_id) : null,
          }, companyId);
        }
      }
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
      // Alta: el listado adopta el registro creado y la ficha sigue abierta ya
      // con id, así se encienden las pestañas que necesitan estar guardado.
      onSave?.(creado);
      setIsDirty(false);
      return true;
    } catch (e) { setError(e.message); return false; }
    finally { setSaving(false); }
  };

  const handleTriggerAgentUpdate = async () => {
    if (!isEditing || !vm?.id) return;
    if (!agentTelemetry.agent_id) {
      setError('Esta VM no tiene agent_id enlazado todavía');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await api.sendAgentUpdateCommand(companyId, {
        ci_type: 'virt_vm',
        ci_id: vm.id,
      });
      markAgentUpdateEnqueued();
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
    } catch (e) {
      setError(e.message || 'No se pudo encolar la actualización del agente');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveDisk = async (disk, idx) => {
    if (disk._new) { setDisks(d => d.filter((_, i) => i !== idx)); return; }
    try { await api.deleteVMDisk(vm.id, disk.id, companyId); setDisks(d => d.filter(x => x.id !== disk.id)); }
    catch (e) { setError(e.message); }
  };
  const handleRemoveNic = async (nic, idx) => {
    if (nic._new) { setNics(n => n.filter((_, i) => i !== idx)); return; }
    try { await api.deleteVMNic(vm.id, nic.id, companyId); setNics(n => n.filter(x => x.id !== nic.id)); }
    catch (e) { setError(e.message); }
  };

  const _safeString = (v) => String(v ?? '').toLowerCase();
  const _sortRows = (rows, sort) => {
    const { key, dir } = sort || {};
    const factor = dir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
      const av = _safeString(a?.[key]);
      const bv = _safeString(b?.[key]);
      if (av < bv) return -1 * factor;
      if (av > bv) return 1 * factor;
      return 0;
    });
  };

  const _toggleSort = (setSort, key) => {
    setSort((prev) => {
      if (prev.key === key) return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      return { key, dir: 'asc' };
    });
  };

  const softwareRows = useMemo(() => {
    const q = softwareSearch.trim().toLowerCase();
    const rows = (agentInventory.installed_software || []).map((entry, idx) => ({
      id: entry.id || `sw-${idx}`,
      name: entry.name || 'Sin nombre',
      version: entry.version || '-',
      publisher: entry.publisher || '-',
    }));
    const filtered = !q
      ? rows
      : rows.filter((row) =>
          [row.name, row.version, row.publisher].some((v) => _safeString(v).includes(q))
        );
    return _sortRows(filtered, softwareSort);
  }, [agentInventory.installed_software, softwareSearch, softwareSort]);

  const serviceRows = useMemo(() => {
    const q = servicesSearch.trim().toLowerCase();
    const rows = (agentInventory.services || []).map((entry, idx) => ({
      id: entry.id || `svc-${idx}`,
      display_name: entry.display_name || entry.name || 'Sin nombre',
      status: entry.status || '-',
      start_type: entry.start_type || '-',
    }));
    const filtered = !q
      ? rows
      : rows.filter((row) =>
          [row.display_name, row.status, row.start_type].some((v) => _safeString(v).includes(q))
        );
    return _sortRows(filtered, servicesSort);
  }, [agentInventory.services, servicesSearch, servicesSort]);

  const inventoryKpis = useMemo(() => {
    const rolesCount = agentInventory.windows_roles.filter(r => r.feature_type === 'Role').length;
    const featuresCount = agentInventory.windows_roles.filter(r => r.feature_type === 'Feature').length;
    return [
      { id: 'kpi-ip', label: 'IPs activas', value: agentInventory.ip_assignments.length, icon: SignalIcon },
      { id: 'kpi-nic', label: 'NICs detectadas', value: nics.length, icon: ServerStackIcon },
      { id: 'kpi-role', label: 'Roles', value: rolesCount, icon: BuildingOfficeIcon },
      { id: 'kpi-feat', label: 'Características', value: featuresCount, icon: CpuChipIcon },
      { id: 'kpi-soft', label: 'Software', value: agentInventory.installed_software.length, icon: CircleStackIcon },
      { id: 'kpi-svc', label: 'Servicios', value: agentInventory.services.length, icon: SparklesIcon },
    ];
  }, [agentInventory, nics.length]);

  const getNicTypeVisual = (nic) => {
    const type = String(nic.adapter_type || nic.nic_type || '').toLowerCase();
    if (type.includes('vmxnet')) return { Icon: SparklesIcon, label: 'VMXNET', tone: 'vm-nic-tone-accent' };
    if (type.includes('virtio')) return { Icon: CpuChipIcon, label: 'VirtIO', tone: 'vm-nic-tone-cyan' };
    if (type.includes('e1000') || type.includes('ethernet')) return { Icon: ServerStackIcon, label: 'Ethernet', tone: 'vm-nic-tone-blue' };
    return { Icon: ComputerDesktopIcon, label: 'NIC', tone: 'vm-nic-tone-neutral' };
  };

  const getNicStatus = (nic) => {
    if (nic.ip_address) return { label: 'Conectada', tone: 'ok' };
    if (nic.port_group_name) return { label: 'Sin IP', tone: 'warn' };
    return { label: 'Desconectada', tone: 'muted' };
  };

  // ── Derived ──
  const powerInfo  = POWER_STATE[form.power_state] || POWER_STATE.unknown;
  const agentInfo = AGENT_STATUS[agentTelemetry.agent_status || 'unknown'] || AGENT_STATUS.unknown;
  const guestInfo = GUEST_AGENT[agentTelemetry.guest_agent || 'unknown'] || GUEST_AGENT.unknown;
  const guestHelpText = GUEST_AGENT_HELP[agentTelemetry.guest_agent || 'unknown'] || GUEST_AGENT_HELP.unknown;
  const hypName    = hypervisors.find(h => String(h.id) === String(form.hypervisor_id))?.name;
  const clusterName = clusters.find(c => String(c.id) === String(form.cluster_id))?.name;
  /* Plataforma que proporciona la máquina. Es una marca, no infraestructura:
     el cliente delegado la ve (L6 Ecosystem, Azure…), pero no el hipervisor.
     Con un proveedor externo tampoco lo ve el dueño: no existe. */
  const providerLabel = hostingProviderLabel(form.hosting_provider);
  const externalProvider = Boolean(
    form.hosting_provider && form.hosting_provider !== HOSTING_PROVIDER_OWN
  );
  const hideHosting = isDelegatedView || externalProvider;
  const ultimaSenal = (() => {
    const v = agentTelemetry?.last_seen;
    if (!v) return '—';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
  })();
  const newDisks   = disks.filter(d => d._new).length;
  const newNics    = nics.filter(n => n._new).length;
  const inventoryCount =
    agentInventory.ip_assignments.length +
    agentInventory.windows_roles.length +
    agentInventory.installed_software.length +
    agentInventory.services.length;

  return (
    <div className="dv-overlay">
      {/* ── TopBar ── */}
      <div className="dv-topbar">
        <button className="dv-back-btn" onClick={() => navGuard.request(() => onClose())}>
          <ArrowLeftIcon />
          Máquinas Virtuales
        </button>
        <DetailCrumb label={form.name || 'Nueva VM'} />
        <FormPager
          items={navItems}
          index={navIndex}
          label="Máquina virtual"
          disabled={saving}
          onChange={(destino) => navGuard.request(() => { setIsDirty(false); onNavigate?.(destino); })}
        />
        {navGuard.dialog}
        <div className="dv-topbar-spacer" />
        {savedOk && (
          <span style={{ color: '#16a34a', fontSize: '0.78rem', fontWeight: 600 }}>✓ Guardado</span>
        )}
        





﻿
        {isDelegatedView && (
          <span
            className="dv-status-badge"
            style={{ background: 'rgba(99,102,241,.12)', color: '#6366f1' }}
            title="La máquina pertenece a otra empresa: se ve, pero no se edita"
          >
            Solo lectura · delegada por {vm?.company_name || 'la empresa propietaria'}
          </span>
        )}
          <span className="dv-status-badge" style={{ background: powerInfo.bg, color: powerInfo.color }}>
          {powerInfo.label}
        </span>
        <div className="dv-topbar-actions">

            {isEditing && !isDelegatedView && agentTelemetry?.agent_id && (
              <div ref={syncActionsMenuRef} style={{ position: 'relative' }}>
                <button
                  className="dv-btn dv-btn-secondary"
                  type="button"
                  onClick={() => setShowSyncActionsMenu((prev) => !prev)}
                  disabled={showSyncModal || syncStep === 'enqueueing' || syncStep === 'pending' || syncStep === 'running'}
                  aria-haspopup="menu"
                  aria-expanded={showSyncActionsMenu}
                  title="Abrir acciones de sincronización y agente"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                >
                  <SparklesIcon style={{ width: 16, height: 16 }} />
                  Sincronizar
                  <ChevronDownIcon style={{ width: 16, height: 16 }} />
                </button>
                {showSyncActionsMenu && (
                  <div
                    role="menu"
                    aria-label="Acciones de sincronización"
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 8px)',
                      right: 0,
                      minWidth: 280,
                      padding: 8,
                      borderRadius: 12,
                      border: '1px solid var(--border)',
                      background: 'var(--bg-card)',
                      boxShadow: '0 18px 38px rgba(15, 23, 42, 0.18)',
                      zIndex: 40,
                      display: 'grid',
                      gap: 6,
                    }}
                  >
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => handleSyncMenuAction('rescan')}
                      style={{
                        display: 'grid',
                        gap: 2,
                        textAlign: 'left',
                        padding: '0.7rem 0.8rem',
                        borderRadius: 10,
                        border: '1px solid transparent',
                        background: 'transparent',
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{ fontWeight: 700 }}>Sincronizar VM</span>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Actualiza nombre, hardware, discos y configuración de esta máquina virtual.</span>
                    </button>
                    
                    <div style={{ height: 1, background: 'var(--border)', margin: '2px 0' }} />
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => handleSyncMenuAction('update_agent')}
                      style={{
                        display: 'grid',
                        gap: 2,
                        textAlign: 'left',
                        padding: '0.7rem 0.8rem',
                        borderRadius: 10,
                        border: '1px solid transparent',
                        background: 'transparent',
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{ fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 8 }}><WrenchScrewdriverIcon style={{ width: 16, height: 16 }} />Actualizar agente</span>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Encola una actualización remota del agente instalado en esta VM.</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          {error && <span style={{ color: '#ef4444', fontSize: 12, marginRight: 8 }}>{error}</span>}
          {!editing ? (
            <>
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon /> Cerrar
              </button>
              {!isDelegatedView && !isCards && (
                <button className="dv-btn dv-btn-primary" type="button" onClick={() => setEditing(true)}>
                  <CheckIcon /> Editar
                </button>
              )}
            </>
          ) : (
            <>
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon /> Descartar
              </button>
              {!isCards && (
              <button className="dv-btn dv-btn-primary" type="button" onClick={handleSave} disabled={saving}>
                <CheckIcon /> {saving ? 'Guardando…' : 'Guardar'}
              </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── SmartBar ── */}
      {/* ── SmartBar (solo ficha clásica; en tarjetas los datos van a badges y chips) ── */}
      {!isCards && (
      <div className="dv-smartbar">
        {!hideHosting && hypName && (
          <button className="dv-smart-btn" style={{ borderColor: '#bbf7d0' }}>
            <span className="dv-smart-btn-icon"><ServerStackIcon style={{ width: 18, height: 18, color: '#16a34a' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: '#16a34a', fontSize: '0.82rem', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{hypName}</span>
            <span className="dv-smart-btn-label">Hypervisor</span>
          </button>
        )}
        {!hideHosting && clusterName && (
          <button className="dv-smart-btn" style={{ borderColor: '#c7d2fe' }}>
            <span className="dv-smart-btn-icon"><CircleStackIcon style={{ width: 18, height: 18, color: '#6366f1' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: '#6366f1', fontSize: '0.82rem', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{clusterName}</span>
            <span className="dv-smart-btn-label">Cluster</span>
          </button>
        )}
        {(form.os_name || form.os_version) && (
          <button className="dv-smart-btn" style={{ borderColor: '#e5e7eb' }}>
            <span className="dv-smart-btn-icon"><ComputerDesktopIcon style={{ width: 18, height: 18, color: '#6b7280' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: '#6b7280', fontSize: '0.82rem', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{form.os_name}{form.os_version ? ` ${form.os_version}` : ''}</span>
            <span className="dv-smart-btn-label">Sistema operativo</span>
          </button>
        )}
        {(form.domain_name != null || form.part_of_domain != null) && (
          <button className="dv-smart-btn" style={{ borderColor: form.part_of_domain ? '#bae6fd' : '#e5e7eb' }}>
            <span className="dv-smart-btn-icon"><BuildingOfficeIcon style={{ width: 18, height: 18, color: form.part_of_domain ? '#0891b2' : '#6b7280' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: form.part_of_domain ? '#0891b2' : '#6b7280', fontSize: '0.82rem', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {form.part_of_domain ? (form.domain_name || 'Dominio') : (form.domain_name || 'Grupo de trabajo')}
            </span>
            <span className="dv-smart-btn-label">{form.part_of_domain ? 'Dominio' : 'Workgroup'}</span>
          </button>
        )}
        {(form.cpu_cores || form.ram_gb) && (
          <button className="dv-smart-btn" style={{ borderColor: '#fde68a' }}>
            <span className="dv-smart-btn-icon"><CpuChipIcon style={{ width: 18, height: 18, color: '#d97706' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: '#d97706', fontSize: '0.82rem' }}>
              {form.cpu_cores ? `${form.cpu_cores} vCPU` : ''}
              {form.cpu_cores && form.ram_gb ? ' · ' : ''}
              {form.ram_gb ? `${form.ram_gb} GB` : ''}
            </span>
            <span className="dv-smart-btn-label">Cómputo</span>
          </button>
        )}
        <button className="dv-smart-btn" style={{ borderColor: '#bfdbfe' }}>
          <span className="dv-smart-btn-icon"><CircleStackIcon style={{ width: 18, height: 18, color: '#2563eb' }} /></span>
          <span className="dv-smart-btn-value" style={{ color: '#2563eb' }}>{disks.length}</span>
          <span className="dv-smart-btn-label">Discos{newDisks > 0 ? ` (+${newDisks})` : ''}</span>
        </button>
        <button className="dv-smart-btn" style={{ borderColor: '#bbf7d0' }}>
          <span className="dv-smart-btn-icon"><SignalIcon style={{ width: 18, height: 18, color: '#059669' }} /></span>
          <span className="dv-smart-btn-value" style={{ color: '#059669' }}>{nics.length}</span>
          <span className="dv-smart-btn-label">NICs{newNics > 0 ? ` (+${newNics})` : ''}</span>
        </button>
        <button className="dv-smart-btn dv-smart-btn-agent" onClick={() => setShowAgentModal(true)} title="Ver detalle del estado del agente">
          <span className="dv-smart-btn-icon"><SignalIcon style={{ width: 18, height: 18, color: agentInfo.color }} /></span>
          <span className="dv-smart-btn-value" style={{ color: agentInfo.color, fontSize: '0.78rem', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {agentInfo.label}
          </span>
          <span className="dv-smart-btn-label">Agente CMDB</span>
        </button>
        {form.is_template && (
          <button className="dv-smart-btn" style={{ borderColor: '#e9d5ff' }}>
            <span className="dv-smart-btn-icon"><ComputerDesktopIcon style={{ width: 18, height: 18, color: '#9333ea' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: '#9333ea', fontSize: '0.82rem' }}>Plantilla</span>
          </button>
        )}
        {vm?.id && (
          <>
            <DocSmartButton elementType="virt_vm" elementId={vm.id} elementName={form.name} companyId={companyId} />
            <ContactsSmartButton
              companyId={companyId}
              objectType="virt_vm"
              objectId={vm.id}
              objectName={form.name}
            />
          </>
        )}
      </div>
      )}

      {/* ── Body ── (en lectura se apaga de golpe con el fieldset) ── */}
      <div className="dv-body">
        <fieldset className={`dv-fieldset${isCards ? ' sf2-main' : ''}`} disabled={isCards ? false : !editing}>
        <div className={`dv-main${isCards ? ' sf2-main' : ''}`}>
          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <>
              <div className="sf2-head">
                <div className="sf2-head-icon" aria-hidden="true">
                  <ComputerDesktopIcon style={{ width: 20, height: 20 }} /></div>
                <div className="sf2-head-title">
                  <div className="sf2-title-row">
                    <input
                      className="sf2-head-input"
                      value={form.name}
                      onChange={e => sf('name', e.target.value)}
                      placeholder="Nombre de la máquina virtual…"
                      disabled={!editing}
                    />
                    {editing ? (
                      <>
                        <button
                          type="button"
                          className="sf2-title-btn sf2-title-btn--ok"
                          onClick={handleSave}
                          disabled={saving}
                          title="Guardar cambios"
                        >
                          {saving ? <span className="dv-spinner" /> : <CheckIcon />}
                        </button>
                        <button
                          type="button"
                          className="sf2-title-btn"
                          onClick={handleCancelEdit}
                          disabled={saving}
                          title="Cancelar la edición y descartar cambios"
                        >
                          <XMarkIcon />
                        </button>
                      </>
                    ) : (!isDelegatedView && (
                      <button type="button" className="sf2-title-btn" onClick={() => setEditing(true)} title="Editar la ficha">
                        <PencilSquareIcon />
                      </button>
                    ))}
                  </div>
                  <div className="sf2-crumb">
                    {[hypName, clusterName].filter(Boolean).join(' › ')
                      || (providerLabel
                        ? [providerLabel, form.hosting_region].filter(Boolean).join(' › ')
                        : (isDelegatedView ? 'Infraestructura externa' : 'Máquina virtual'))}
                  </div>
                </div>
                <div className="sf2-badges">
                  <span className="sf2-badge"><i style={{ '--c': powerInfo.color }} />{powerInfo.label}</span>
                  <span className="sf2-badge"><i style={{ '--c': agentInfo.color }} />Agente: {agentInfo.label}</span>
                  {(form.os_name || form.os_version) && (
                    <span className="sf2-badge">{form.os_name}{form.os_version ? ` ${form.os_version}` : ''}</span>
                  )}
                  {form.is_template && <span className="sf2-badge">Plantilla</span>}
                </div>
              </div>
              <div className="sf2-chips">
                {!hideHosting && hypName && (
                  <span className="sf2-chip sf2-chip--flat"><b>{hypName}</b><span>hypervisor</span></span>
                )}
                {!hideHosting && clusterName && (
                  <span className="sf2-chip sf2-chip--flat"><b>{clusterName}</b><span>clúster</span></span>
                )}
                {providerLabel && (
                  <span className="sf2-chip sf2-chip--flat" title="Quién proporciona la plataforma donde corre la máquina">
                    <b>{providerLabel}</b><span>proveedor</span>
                  </span>
                )}
                {!providerLabel && isDelegatedView && (
                  <span className="sf2-chip sf2-chip--flat" title="La plataforma del proveedor no forma parte del inventario de tu empresa">
                    <b>Infraestructura externa</b><span>alojamiento</span>
                  </span>
                )}
                {(form.cpu_cores || form.ram_gb) && (
                  <span className="sf2-chip sf2-chip--flat">
                    <b>{[form.cpu_cores ? `${form.cpu_cores} vCPU` : null, form.ram_gb ? `${form.ram_gb} GB` : null].filter(Boolean).join(' · ')}</b>
                    <span>cómputo</span>
                  </span>
                )}
                <span className="sf2-chip sf2-chip--flat"><b>{disks.length}</b><span>discos</span></span>
                <span className="sf2-chip sf2-chip--flat"><b>{nics.length}</b><span>NICs</span></span>
                <button type="button" className="sf2-chip" onClick={() => setTab('resumen')}>
                  <b>{((agentInventory.ip_assignments || []).length)}</b><span>IPs detectadas</span>
                </button>
                {vm?.id && (
                  <DocSmartButton elementType="virt_vm" elementId={vm.id} elementName={form.name} companyId={companyId} variant="chip" />
                )}
              </div>
              </>
            ) : (
              <input
                className="dv-record-title"
                value={form.name}
                onChange={e => sf('name', e.target.value)}
                placeholder="Nombre de la máquina virtual…"
              />
            )}
            tabs={(
              <div className="dv-tabs-nav">
            {[
              ...(isCards
                ? [{ id: 'resumen', label: 'General' }]
                : [{ id: 'general', label: 'General' }, { id: 'compute', label: 'Cómputo' }]),
              { id: 'disks',   label: isCards ? `Almacenamiento (${disks.length})` : `Discos (${disks.length})` },
              { id: 'nics',    label: `NICs (${nics.length})` },
              { id: 'inventory', label: `Inventario (${inventoryCount})` },
              { id: 'ad',      label: form.ad_dc ? '🏢 Active Directory •' : '🏢 Active Directory' },
              ...(isEditing ? [{ id: 'access', label: `Accesos${accessCount > 0 ? ` (${accessCount})` : ''}` }] : []),
              ...(!isCards && isEditing ? [{ id: 'almacenamiento', label: `Almacenamiento (${volumeCount})` }] : []),
              { id: 'monitoring', label: 'Monitorización' },
              // Misma consola que en la ficha de una estación: rescan, actualizar
              // agente, log y cola de comandos del equipo de dentro de la VM.
              { id: 'agente',   label: 'Agente' },
              { id: 'deleg',   label: 'Delegación' },
            ].filter(t => !(t.id === 'deleg' && isDelegatedView)).map(t => (
              <TabButton
                key={t.id}
                className={`dv-tab-btn${tab === t.id ? ' active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </TabButton>
            ))}
              </div>
            )}
          >
          <fieldset className="dv-fieldset" disabled={!editing && tab !== 'agente'}>

          {/* ── Tab: Resumen (solo tarjetas) ── */}
          {isCards && tab === 'resumen' && (
            <div className="dv-tab-pane">
              {vm?.id && (
                <div style={{ marginBottom: 16 }}>
                  <RemoteActions companyId={companyId} ciType="virt_vm" ciId={vm.id} />
                </div>
              )}
              <div className="sf2-grid sf2-grid--2">
                <div className="sf2-card">
                  <h3>General{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field"><label>Nombre</label><div className="v">{form.name || '—'}</div></div>
                    <div className="sf2-field">
                      <label>Tipo</label>
                      {editing ? (
                        <select value={form.vm_type} onChange={e => sf('vm_type', e.target.value)}>
                          {VM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      ) : (
                        <div className="v">{VM_TYPES.find(t => t.value === form.vm_type)?.label || form.vm_type || '—'}</div>
                      )}
                    </div>
                    <div className="sf2-field">
                      <label>SO (nombre)</label>
                      {editing
                        ? <input value={form.os_name || ''} onChange={e => sf('os_name', e.target.value)} placeholder="ej. Windows Server" />
                        : <div className="v">{form.os_name || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>SO (versión)</label>
                      {editing
                        ? <input value={form.os_version || ''} onChange={e => sf('os_version', e.target.value)} placeholder="ej. 2022 Datacenter" />
                        : <div className="v">{form.os_version || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>{form.part_of_domain ? 'Dominio' : 'Grupo de trabajo'}</label>
                      {editing
                        ? <input value={form.domain_name || ''} onChange={e => sf('domain_name', e.target.value)} placeholder="ej. corp.empresa.local" />
                        : <div className="v">{form.domain_name || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Estado energético</label>
                      {editing ? (
                        <select value={form.power_state} onChange={e => sf('power_state', e.target.value)}>
                          <option value="on">Encendida</option>
                          <option value="off">Apagada</option>
                          <option value="suspended">Suspendida</option>
                          <option value="unknown">Desconocido</option>
                        </select>
                      ) : <div className="v">{powerInfo.label}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Estado del registro</label>
                      {editing ? (
                        <select value={form.status} onChange={e => sf('status', e.target.value)}>
                          <option value="active">Activo</option>
                          <option value="archived">Archivado</option>
                          <option value="decom">Decom</option>
                        </select>
                      ) : <div className="v">{{ active: 'Activo', archived: 'Archivado', decom: 'Decom' }[form.status] || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Pertenece a dominio</label>
                      <label className="sf2-switch sf2-switch--sm">
                        <input type="checkbox" checked={!!form.part_of_domain} onChange={e => sf('part_of_domain', e.target.checked)} disabled={!editing} />
                        <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                        <span className="sf2-switch-label">{form.part_of_domain ? 'Sí' : 'No'}</span>
                      </label>
                    </div>
                    <div className="sf2-field">
                      <label>Plantilla</label>
                      <label className="sf2-switch sf2-switch--sm">
                        <input type="checkbox" checked={!!form.is_template} onChange={e => sf('is_template', e.target.checked)} disabled={!editing} />
                        <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                        <span className="sf2-switch-label">{form.is_template ? 'Sí' : 'No'}</span>
                      </label>
                    </div>
                    <div className="sf2-field">
                      <label>Protección HA</label>
                      <label className="sf2-switch sf2-switch--sm">
                        <input type="checkbox" checked={!!form.ha_protected} onChange={e => sf('ha_protected', e.target.checked)} disabled={!editing} />
                        <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                        <span className="sf2-switch-label">{form.ha_protected ? 'Sí' : 'No'}</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="sf2-card">
                  <h3>Cómputo{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field">
                      <label>vCPUs</label>
                      {editing
                        ? <input type="number" value={form.cpu_cores ?? ''} onChange={e => sf('cpu_cores', e.target.value)} placeholder="ej. 8" />
                        : <div className="v">{form.cpu_cores || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>RAM (GB)</label>
                      {editing
                        ? <input type="number" value={form.ram_gb ?? ''} onChange={e => sf('ram_gb', e.target.value)} placeholder="ej. 16" />
                        : <div className="v">{form.ram_gb ? `${form.ram_gb} GB` : '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Proveedor</label>
                      {editing ? (
                        <select value={form.hosting_provider || ''} onChange={e => setHostingProvider(e.target.value)}>
                          <option value="">— Sin declarar —</option>
                          {HOSTING_PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </select>
                      ) : <div className="v">{providerLabel || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Región</label>
                      {editing
                        ? <input value={form.hosting_region || ''} onChange={e => sf('hosting_region', e.target.value)} placeholder="ej. westeurope" />
                        : <div className="v">{form.hosting_region || '—'}</div>}
                    </div>
                    {!hideHosting && (
                      <div className="sf2-field">
                        <label>Hypervisor</label>
                        {editing ? (
                          <select value={form.hypervisor_id} onChange={e => sf('hypervisor_id', e.target.value)}>
                            <option value="">— Sin asignar —</option>
                            {hypervisors.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                          </select>
                        ) : <div className="v">{hypName || '—'}</div>}
                      </div>
                    )}
                    {!hideHosting && (
                      <div className="sf2-field">
                        <label>Clúster</label>
                        {editing ? (
                          <select value={form.cluster_id} onChange={e => sf('cluster_id', e.target.value)}>
                            <option value="">— Sin cluster —</option>
                            {clusters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                        ) : <div className="v">{clusterName || '—'}</div>}
                      </div>
                    )}
                    <div className="sf2-field"><label>Discos</label><div className="v">{disks.length} · {disks.reduce((a, d) => a + (Number(d.size_gb) || 0), 0)} GB</div></div>
                    <div className="sf2-field"><label>NICs</label><div className="v">{nics.length}</div></div>
                  </div>
                </div>

                <div className="sf2-card">
                  <h3>Estado y monitorización</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field"><label>Agente</label><div className="v">{agentInfo.label}</div></div>
                    <div className="sf2-field"><label>Última señal</label><div className="v">{ultimaSenal}</div></div>
                    <div className="sf2-field"><label>IPs detectadas</label><div className="v">{(agentInventory.ip_assignments || []).length}</div></div>
                    <div className="sf2-field"><label>Servicios</label><div className="v">{(agentInventory.services || []).length}</div></div>
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <button type="button" className="sf2-link" onClick={() => setTab('agente')}>Ver detalle del agente →</button>
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <button type="button" className="sf2-link" onClick={() => setTab('monitoring')}>Ver monitorización completa →</button>
                  </div>
                </div>

                <div className="sf2-card">
                  <h3>Equipo según el agente</h3>
                  {(form.manufacturer || form.serial_number || form.install_version) ? (
                    <div className="sf2-fields" style={{ '--n': 2 }}>
                      <div className="sf2-field"><label>Fabricante</label><div className="v">{form.manufacturer || '—'}</div></div>
                      <div className="sf2-field"><label>Modelo</label><div className="v">{form.model || '—'}</div></div>
                      <div className="sf2-field"><label>Nº de serie</label><div className="v">{form.serial_number || '—'}</div></div>
                      <div className="sf2-field"><label>CPU</label><div className="v">{form.cpu || '—'}</div></div>
                      <div className="sf2-field"><label>IP del sistema</label><div className="v">{form.ip_address || '—'}</div></div>
                      <div className="sf2-field"><label>MAC</label><div className="v">{form.mac_address || '—'}</div></div>
                      <div className="sf2-field">
                        <label>BIOS</label>
                        <div className="v">
                          {form.bios_version || '—'}
                          {form.bios_release_date ? ` · ${form.bios_release_date}` : ''}
                        </div>
                      </div>
                      <div className="sf2-field"><label>SO instalado</label><div className="v">{form.os_install_date || '—'}</div></div>
                      <div className="sf2-field">
                        <label>Licencia de Windows</label>
                        <div className="v">
                          {[form.license_status, form.license_channel, form.license_partial_key]
                            .filter(Boolean)
                            .join(' · ') || '—'}
                        </div>
                      </div>
                      <div className="sf2-field">
                        <label>Agente instalado</label>
                        <div className="v">
                          {form.install_channel
                            ? `${form.install_channel.toUpperCase()} ${form.install_version || ''}`.trim()
                            : '—'}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="sf2-hint" style={{ margin: 0 }}>
                      Todavía no hay inventario del agente dentro de esta máquina. El del hipervisor no basta
                      para hardware, BIOS ni licencia: instala el agente con el MSI o el EXE y aparecerán aquí.
                    </p>
                  )}
                </div>

                <div className="sf2-card">
                  <h3>Inventario detectado por el agente</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field"><label>Volúmenes montados</label><div className="v">{disks.length}</div></div>
                    <div className="sf2-field"><label>NICs detectadas</label><div className="v">{nics.length}</div></div>
                    <div className="sf2-field"><label>Roles / features</label><div className="v">{(agentInventory.windows_roles || []).length}</div></div>
                    <div className="sf2-field"><label>Software</label><div className="v">{(agentInventory.installed_software || []).length}</div></div>
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <button type="button" className="sf2-link" onClick={() => setTab('inventory')}>Ver inventario →</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: General ── */}
          {tab === 'general' && (
            <div className="dv-tab-pane">
              <div className="dv-field-grid">
                <div className="dv-field-group">
                  <label className="dv-label">Tipo de VM</label>
                  <select className="dv-select" value={form.vm_type} onChange={e => sf('vm_type', e.target.value)}>
                    {VM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Estado energético</label>
                  <select className="dv-select" value={form.power_state} onChange={e => sf('power_state', e.target.value)}>
                    <option value="on">Encendida</option>
                    <option value="off">Apagada</option>
                    <option value="suspended">Suspendida</option>
                    <option value="unknown">Desconocido</option>
                  </select>
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">SO (nombre)</label>
                  <input className="dv-input" value={form.os_name} onChange={e => sf('os_name', e.target.value)} placeholder="ej. Windows Server" />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">SO (versión)</label>
                  <input className="dv-input" value={form.os_version} onChange={e => sf('os_version', e.target.value)} placeholder="ej. 2022 Datacenter" />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Proveedor de plataforma</label>
                  <select className="dv-select" value={form.hosting_provider || ''} onChange={e => setHostingProvider(e.target.value)}>
                    <option value="">— Sin declarar —</option>
                    {HOSTING_PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Región</label>
                  <input className="dv-input" value={form.hosting_region || ''} onChange={e => sf('hosting_region', e.target.value)} placeholder="ej. westeurope" />
                </div>
                {!hideHosting && (
                  <div className="dv-field-group">
                    <label className="dv-label">Hypervisor host</label>
                    <select className="dv-select" value={form.hypervisor_id} onChange={e => sf('hypervisor_id', e.target.value)}>
                      <option value="">— Sin asignar —</option>
                      {hypervisors.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                    </select>
                  </div>
                )}
                {!hideHosting && (
                  <div className="dv-field-group">
                    <label className="dv-label">Cluster</label>
                    <select className="dv-select" value={form.cluster_id} onChange={e => sf('cluster_id', e.target.value)}>
                      <option value="">— Sin cluster —</option>
                      {clusters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                )}
                <div className="dv-field-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="dv-label">UUID (VMware/Proxmox/etc.)</label>
                  <input className="dv-input" style={{ fontFamily: 'monospace', fontSize: 12 }} value={form.uuid} onChange={e => sf('uuid', e.target.value)} placeholder="ej. 502aab23-..." />
                </div>
                <div className="dv-field-group dv-col-span-all">
                  <label className="dv-label">Estado del agente en la VM</label>
                  <div className="vm-agent-inline-card">
                    <span className="vm-badge" style={{ background: agentInfo.bg, color: agentInfo.color }}>
                      {agentInfo.label}
                    </span>
                    <span className="vm-guest-chip" style={{ color: guestInfo.color }}>
                      {guestInfo.label}
                    </span>
                    {agentTelemetry.last_seen_at && (
                      <span className="vm-meta-pill">
                        Última vez visto: {new Date(agentTelemetry.last_seen_at).toLocaleString()}
                      </span>
                    )}
                    {agentTelemetry.agent_id && (
                      <span className="vm-meta-pill vm-meta-mono">
                        agent_id: {agentTelemetry.agent_id}
                      </span>
                    )}
                    {agentTelemetry.agent_version && (
                      <span className="vm-meta-pill vm-meta-mono">
                        version: {agentTelemetry.agent_version}
                      </span>
                    )}
                    <button type="button" className="dv-btn dv-btn-secondary" onClick={() => setShowAgentModal(true)}>
                      Ver detalle
                    </button>
                  </div>
                </div>
                <div className="dv-field-group dv-col-span-all">
                  <div className="sf2-switches">
                    {[
                      ['is_template',  'Es plantilla (template)'],
                      ['ha_protected', 'Protección HA'],
                    ].map(([k, l]) => (
                      <label key={k} className="sf2-switch">
                        <input type="checkbox" checked={form[k]} onChange={e => sf(k, e.target.checked)} />
                        <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                        <span className="sf2-switch-label">{l}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {(agentBackedDisks || agentBackedNics || inventoryCount > 0) && (
                  <div className="dv-field-group dv-col-span-all">
                    <label className="dv-label">Inventario detectado por el agente</label>
                    <div className="vm-agent-stats-grid">
                      <span>Volúmenes montados: {disks.length}</span>
                      <span>NICs detectadas: {nics.length}</span>
                      <span>IPs activas: {agentInventory.ip_assignments.length}</span>
                      <span>Roles/features: {agentInventory.windows_roles.length}</span>
                      <span>Software: {agentInventory.installed_software.length}</span>
                      <span>Servicios: {agentInventory.services.length}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Tab: Almacenamiento ── */}
          {tab === 'almacenamiento' && isEditing && (
            <div className="dv-tab-pane">
              <StorageVolumesPanel
                ciType="virt_vm"
                ciId={vm?.id}
                companyId={companyId}
                luns={companyLuns}
                onChanged={refreshVolumes}
              />
            </div>
          )}

          {/* ── Tab: Active Directory ── */}
          {tab === 'ad' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <div className="dv-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  Active Directory
                  {form.ad_dc && (
                    <span style={{
                      fontSize: '0.7rem', fontWeight: 700,
                      background: 'rgba(59,130,246,.18)', color: '#93c5fd',
                      borderRadius: 4, padding: '1px 7px', textTransform: 'uppercase', letterSpacing: '.04em',
                    }}>
                      Domain Controller
                    </span>
                  )}
                </div>
                <p className="dv-hint" style={{ marginBottom: 10 }}>
                  Marca esta VM como Domain Controller para que aparezca en el esquema de
                  Active Directory (pestaña <strong>🌐 Topología AD</strong> del servicio AD
                  al que la asignes).
                </p>
                <div className="dv-field-grid">
                  <div className="dv-field-group dv-col-span-all">
                    <div className="sf2-switches">
                      <label className="sf2-switch">
                        <input
                          type="checkbox"
                          checked={!!form.ad_dc}
                          onChange={e => sf('ad_dc', e.target.checked)}
                        />
                        <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                        <span className="sf2-switch-label">Esta máquina virtual es un Domain Controller</span>
                      </label>
                    </div>
                  </div>
                  {form.ad_dc && (
                    <>
                      <div className="dv-field-group">
                        <label className="dv-label">Dominio (FQDN)</label>
                        <input
                          className="dv-input"
                          placeholder="ej. corp.empresa.local"
                          value={form.ad_domain || ''}
                          onChange={e => sf('ad_domain', e.target.value)}
                        />
                      </div>
                      <div className="dv-field-group">
                        <label className="dv-label">Sitio AD</label>
                        <input
                          className="dv-input"
                          placeholder="ej. Madrid / Barcelona"
                          value={form.ad_site || ''}
                          onChange={e => sf('ad_site', e.target.value)}
                        />
                        {!hideHosting && (
                          <span className="dv-hint">
                            Si lo dejas vacío se usa la sede del host (hypervisor → rack → sede).
                          </span>
                        )}
                      </div>
                      <div className="dv-field-group dv-col-span-all">
                        <div className="sf2-switches">
                          <label className="sf2-switch">
                            <input
                              type="checkbox"
                              checked={!!form.ad_global_catalog}
                              onChange={e => sf('ad_global_catalog', e.target.checked)}
                            />
                            <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                            <span className="sf2-switch-label">Es Global Catalog (GC)</span>
                          </label>
                        </div>
                      </div>
                      <div className="dv-field-group dv-col-span-all">
                        <label className="dv-label">Roles FSMO</label>
                        <div className="sf2-switches">
                          {FSMO_ROLE_OPTIONS.map(([value, label]) => {
                            const current = Array.isArray(form.ad_fsmo_roles) ? form.ad_fsmo_roles : [];
                            return (
                              <label key={value} className="sf2-switch">
                                <input
                                  type="checkbox"
                                  checked={current.includes(value)}
                                  onChange={e => {
                                    const next = e.target.checked
                                      ? [...new Set([...current, value])]
                                      : current.filter(v => v !== value);
                                    sf('ad_fsmo_roles', next);
                                  }}
                                />
                                <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                                <span className="sf2-switch-label">{label}</span>
                              </label>
                            );
                          })}
                        </div>
                        <span className="dv-hint">
                          Cada rol FSMO debería estar en un solo DC de todo el bosque. La vista de
                          topología avisa si detecta conflictos o roles sin asignar.
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Lo que el agente lee DENTRO de la máquina: DN/OU, grupos, GPO
                  aplicadas, DC con el que habla y claves de BitLocker. Es el
                  mismo panel de la pestaña Active Directory de una estación. */}
              <div className="dv-section">
                <WorkstationAdPanel
                  companyId={companyId}
                  ciType="virt_vm"
                  workstation={{
                    ...vm,
                    ad_client: agentAdClient,
                    agent_id: agentTelemetry.agent_id,
                  }}
                />
              </div>
            </div>
          )}

          {/* ── Tab: Accesos ── */}
          {tab === 'access' && isEditing && (
            <div className="dv-tab-pane">
              <DeviceAccessPanel
                objectType="virt_vm"
                objectId={vm?.id}
                companyId={companyId}
                elementName={vm?.name || form.name}
                defaultHost={vm?.ip_address || ''}
                onCountChange={setAccessCount}
                canEdit={editing}
              />
            </div>
          )}

          {/* ── Tab: Cómputo ── */}
          {tab === 'compute' && (
            <div className="dv-tab-pane">
              <div className="dv-field-grid">
                <div className="dv-field-group">
                  <label className="dv-label">vCPUs (cores)</label>
                  <input type="number" className="dv-input" value={form.cpu_cores} onChange={e => sf('cpu_cores', e.target.value)} placeholder="ej. 8" />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">RAM (GB)</label>
                  <input type="number" className="dv-input" value={form.ram_gb} onChange={e => sf('ram_gb', e.target.value)} placeholder="ej. 16" />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Estado del registro</label>
                  <select className="dv-select" value={form.status} onChange={e => sf('status', e.target.value)}>
                    <option value="active">Activo</option>
                    <option value="archived">Archivado</option>
                    <option value="decom">Decom</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Discos ── */}
          {tab === 'disks' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
              <div className="dv-section-title">Discos de la máquina virtual</div>
              <div className="sf2-hl" style={{ marginBottom: 12 }}>
                <div className="sf2-kpi"><span>Discos</span><b>{disks.length}</b></div>
                <div className="sf2-kpi"><span>Capacidad total</span><b>{disks.reduce((a, d) => a + (Number(d.size_gb) || 0), 0)} GB</b></div>
                <div className="sf2-kpi"><span>Añadidos sin guardar</span><b>{newDisks}</b></div>
              </div>
              {agentBackedDisks && (
                <div className="vm-readonly-banner">
                  Vista en solo lectura basada en los volúmenes montados detectados dentro del SO invitado.
                </div>
              )}
              <div className="sf2-table-wrap">
              <table className="vm-data-table">
                <thead>
                  <tr>
                    {['Etiqueta', 'Tamaño (GB)', 'Formato', 'Datastore', 'Bus', ''].map(h => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {disks.map((d, i) => (
                    <tr key={i}>
                      <td>
                        {d._new
                          ? <input className="dv-input vm-input-compact" value={d.label} onChange={e => setDisks(ds => ds.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} placeholder="disco-01" />
                          : d.label || d.id}
                      </td>
                      <td>
                        {d._new
                          ? <input type="number" className="dv-input vm-input-compact vm-input-xs" value={d.size_gb} onChange={e => setDisks(ds => ds.map((x, j) => j === i ? { ...x, size_gb: e.target.value } : x))} />
                          : `${d.size_gb} GB`}
                      </td>
                      <td>
                        {d._new
                          ? <select className="dv-select vm-input-compact" value={d.disk_format} onChange={e => setDisks(ds => ds.map((x, j) => j === i ? { ...x, disk_format: e.target.value } : x))}>
                              {DISK_FORMATS.map(f => <option key={f}>{f}</option>)}
                            </select>
                          : d.disk_format}
                      </td>
                      <td>
                        {d._new
                          ? <input className="dv-input vm-input-compact" value={d.datastore} onChange={e => setDisks(ds => ds.map((x, j) => j === i ? { ...x, datastore: e.target.value } : x))} />
                          : d.datastore || '—'}
                      </td>
                      <td>
                        {d._new
                          ? <input className="dv-input vm-input-compact vm-input-xs" value={d.bus_type} onChange={e => setDisks(ds => ds.map((x, j) => j === i ? { ...x, bus_type: e.target.value } : x))} />
                          : d.bus_type || 'scsi'}
                      </td>
                      <td>
                        {!d._agent && (
                          <button onClick={() => handleRemoveDisk(d, i)} className="vm-icon-btn vm-icon-btn-danger">
                            <TrashIcon style={{ width: 14, height: 14 }} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {!agentBackedDisks && (
                <button
                  onClick={() => setDisks(d => [...d, { ...EMPTY_DISK }])}
                  className="vm-dashed-btn"
                >
                  <PlusIcon style={{ width: 14, height: 14 }} /> Añadir disco
                </button>
              )}
              </div>
              {isCards && (
                <div className="dv-section">
                  <div className="dv-section-title">Volúmenes del equipo</div>
                  <StorageVolumesPanel
                    ciType="virt_vm"
                    ciId={vm?.id}
                    companyId={companyId}
                    luns={companyLuns}
                    onChanged={refreshVolumes}
                  />
                </div>
              )}
            </div>
          )}

          {/* ── Tab: NICs ── */}
          {tab === 'nics' && (
            <div className="dv-tab-pane">
              {agentBackedNics && (
                <div className="vm-readonly-banner">
                  Vista en solo lectura basada en las NICs detectadas por el agente dentro de la VM.
                </div>
              )}

              {/* Existing / added NICs table */}
              {nics.length > 0 && (
                <table className="vm-data-table vm-data-table-nics">
                  <thead>
                    <tr>
                      {['Nombre', 'Tipo', 'MAC', 'Port Group / VLAN', 'IP asignada', ''].map(h => (
                        <th key={h}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {nics.map((n, i) => (
                      <tr key={i}>
                        <td className="vm-nic-name-cell">
                          {deriveNicName(n, i)}
                          {n._new && <span className="vm-mini-tag">nuevo</span>}
                        </td>
                        <td>
                          {(() => {
                            const visual = getNicTypeVisual(n);
                            const status = getNicStatus(n);
                            const IconCmp = visual.Icon;
                            return (
                              <div className="vm-nic-type-wrap">
                                <span className={`vm-nic-type-badge ${visual.tone}`}>
                                  <IconCmp style={{ width: 13, height: 13 }} />
                                  {visual.label}
                                </span>
                                <span className={`vm-nic-status vm-nic-status-${status.tone}`}>{status.label}</span>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="vm-cell-mono vm-cell-subtle">{n.mac_address || '—'}</td>
                        <td>
                          {n.port_group_name
                            ? <span className="vm-stack-inline">
                                <span className="vm-nic-pg-name">{n.port_group_name}</span>
                                {n.vlan_id && <span className="vm-nic-vlan-chip">VLAN {n.vlan_id}</span>}
                                {n.vswitch_name && <span className="vm-nic-vswitch">{n.vswitch_name}</span>}
                              </span>
                            : <span className="vm-empty-inline">Sin port group</span>}
                        </td>
                        <td className={`vm-cell-mono ${n.ip_address ? 'vm-cell-ip' : 'vm-cell-subtle'}`}>
                          {n.ip_address || <span className="vm-empty-inline">Sin IP</span>}
                        </td>
                        <td>
                          <div className="vm-inline-actions">
                            {!n._agent && (
                              <>
                                <button
                                  title="Editar NIC"
                                  onClick={() => openEditNic(n, i)}
                                  className="vm-icon-btn vm-icon-btn-primary"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 14, height: 14 }}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                </button>
                                <button onClick={() => handleRemoveNic(n, i)} className="vm-icon-btn vm-icon-btn-danger">
                                  <TrashIcon style={{ width: 14, height: 14 }} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Add NIC inline form */}
              {!agentBackedNics && nicDraft !== null ? (
                <div style={{ background: 'rgba(16,185,129,.15)', border: '1px solid rgba(16,185,129,.4)', borderRadius: 8, padding: 16, marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#065f46', marginBottom: 12 }}>{nicBeingEdited ? '✏️ Editar tarjeta de red' : 'Nueva tarjeta de red'}</div>

                  {/* Row 1: label, type, MAC */}
                  <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                    <div style={{ flex: '2 1 150px' }}>
                      <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 3 }}>Etiqueta *</label>
                      <input
                        className="form-control form-control-sm"
                        value={nicDraft.nic_label}
                        onChange={e => setNicDraft(d => ({ ...d, nic_label: e.target.value }))}
                        placeholder="ej. eth0 / Network adapter 1"
                        autoFocus
                      />
                    </div>
                    <div style={{ flex: '1 1 120px' }}>
                      <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 3 }}>Tipo adaptador</label>
                      <select
                        className="form-select form-select-sm"
                        value={nicDraft.adapter_type}
                        onChange={e => setNicDraft(d => ({ ...d, adapter_type: e.target.value }))}
                      >
                        {NIC_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <div style={{ flex: '2 1 160px' }}>
                      <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 3 }}>MAC address</label>
                      <input
                        className="form-control form-control-sm"
                        style={{ fontFamily: 'monospace' }}
                        value={nicDraft.mac_address}
                        onChange={e => setNicDraft(d => ({ ...d, mac_address: e.target.value }))}
                        placeholder="00:50:56:xx:xx:xx (opcional)"
                      />
                    </div>
                  </div>

                  {/* Row 2: Port Group */}
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 3 }}>Port Group</label>
                    {portGroups.length === 0
                      ? <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No hay port groups definidos. Crea vSwitches con port groups primero.</div>
                      : <select
                          className="form-select form-select-sm"
                          value={nicDraft.port_group_id}
                          onChange={e => setNicDraft(d => ({ ...d, port_group_id: e.target.value }))}
                        >
                          <option value="">— Sin port group —</option>
                          {(() => {
                            // Group by vswitch_name
                            const byVs = {};
                            portGroups.forEach(pg => {
                              const k = pg.vswitch_name || 'Sin vSwitch';
                              if (!byVs[k]) byVs[k] = [];
                              byVs[k].push(pg);
                            });
                            return Object.entries(byVs).map(([vsName, pgs]) => (
                              <optgroup key={vsName} label={`🔀 ${vsName}`}>
                                {pgs.map(pg => (
                                  <option key={pg.id} value={pg.id}>
                                    {pg.name}{pg.vlan_id ? ` (VLAN ${pg.vlan_id})` : ''}
                                  </option>
                                ))}
                              </optgroup>
                            ));
                          })()}
                        </select>
                    }
                  </div>

                  {/* Row 3: IP from IPAM — 3-mode picker */}
                  <div style={{ marginBottom: 12, background: '#f8fffe', border: '1px solid #d1fae5', borderRadius: 7, padding: '10px 12px' }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#065f46', display: 'block', marginBottom: 7 }}>IP (desde IPAM)</label>

                    {/* mode selector */}
                    <div style={{ display: 'flex', gap: 14, marginBottom: 8, flexWrap: 'wrap' }}>
                      {[
                        ['auto',   'Auto-asignar (próxima libre)'],
                        ['manual', 'IP manual'],
                        ['map',    '🗺️ Mapa de IPs'],
                      ].map(([v, lbl]) => (
                        <label key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 12, fontWeight: nicIpMode === v ? 700 : 400, color: nicIpMode === v ? '#065f46' : '#6b7280' }}>
                          <input
                            type="radio" name="nicIpMode" value={v} checked={nicIpMode === v}
                            onChange={() => {
                              setNicIpMode(v);
                              setNicDraft(d => d ? { ...d, ip_address: '' } : d);
                              setNicNextIp('');
                              setNicMapData(null);
                              setNicIpValidation({ format: null, inSubnet: null, occupied: null, checking: false });
                            }}
                          />
                          {lbl}
                        </label>
                      ))}
                    </div>

                    {/* subnet selector — always visible */}
                    <div style={{ marginBottom: 8 }}>
                      <label style={{ fontSize: 11, color: '#6b7280', display: 'block', marginBottom: 2 }}>Subred</label>
                      <select
                        className="form-select form-select-sm"
                        value={nicSubnet}
                        onChange={e => {
                          setNicSubnet(e.target.value);
                          setNicNextIp('');
                          setNicDraft(d => d ? { ...d, ip_address: '' } : d);
                          setNicMapData(null);
                        }}
                      >
                        <option value="">— Elige subred —</option>
                        {ipamSubnets.map(s => (
                          <option key={s.id} value={s.id}>{s.cidr}{s.name ? ` — ${s.name}` : ''}</option>
                        ))}
                      </select>
                    </div>

                    {/* AUTO mode */}
                    {nicIpMode === 'auto' && (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: 11, color: '#6b7280', display: 'block', marginBottom: 2 }}>Próxima IP libre</label>
                          <div style={{
                            background: nicDraft?.ip_address ? '#f0fdf4' : '#f9fafb',
                            border: `1px solid ${nicDraft?.ip_address ? '#86efac' : '#e5e7eb'}`,
                            borderRadius: 6, padding: '6px 10px', fontFamily: 'monospace',
                            fontSize: 13, color: nicDraft?.ip_address ? '#15803d' : '#9ca3af',
                            fontWeight: nicDraft?.ip_address ? 700 : 400, minHeight: 33,
                          }}>
                            {nicNextIpLoading
                              ? <span style={{ color: '#9ca3af' }}>Calculando…</span>
                              : nicDraft?.ip_address
                                ? <span style={{ color: '#059669', fontWeight: 700 }}>{nicDraft.ip_address}</span>
                                : <span style={{ color: '#9ca3af' }}>Elige una subred</span>}
                          </div>
                        </div>
                        <button
                          type="button" className="btn btn-outline-secondary btn-sm"
                          style={{ fontSize: 12, whiteSpace: 'nowrap', alignSelf: 'flex-end' }}
                          disabled={!nicSubnet || nicNextIpLoading}
                          onClick={autoFillIp}
                        >
                          {nicNextIpLoading ? '⏳ Buscando…' : '✨ Siguiente libre'}
                        </button>
                      </div>
                    )}

                    {/* MANUAL mode */}
                    {nicIpMode === 'manual' && (
                      <div>
                        <label style={{ fontSize: 11, color: '#6b7280', display: 'block', marginBottom: 2 }}>
                          Dirección IP
                          {nicIpValidation.format === true && (
                            <span style={{ marginLeft: 6, color: nicIpValidation.inSubnet === false ? '#dc2626' : '#059669', fontSize: 11 }}>
                              {nicIpValidation.inSubnet === false ? '⚠ fuera de subred' : nicIpValidation.inSubnet === true ? '✔ en subred' : '✔ formato OK'}
                            </span>
                          )}
                          {nicIpValidation.format === false && (
                            <span style={{ marginLeft: 6, color: '#dc2626', fontSize: 11 }}>⚠ IP inválida</span>
                          )}
                        </label>
                        <input
                          className={`form-control form-control-sm${
                            nicIpValidation.format === false || nicIpValidation.inSubnet === false
                              ? ' is-invalid'
                              : (nicIpValidation.occupied && nicIpValidation.occupied !== 'free' && nicIpValidation.occupied !== 'reserved')
                                ? ' is-invalid'
                                : (nicIpValidation.format === true && nicIpValidation.inSubnet !== false && !nicIpValidation.checking && nicDraft?.ip_address)
                                  ? ' is-valid'
                                  : ''
                          }`}
                          style={{ fontFamily: 'monospace' }}
                          value={nicDraft?.ip_address || ''}
                          onChange={e => setNicDraft(d => ({ ...d, ip_address: e.target.value }))}
                          placeholder="ej. 10.0.1.50"
                        />
                        {nicIpValidation.checking && <div style={{ marginTop: 4, fontSize: 11, color: '#6b7280' }}>⏳ Comprobando disponibilidad en IPAM…</div>}
                        {!nicIpValidation.checking && nicIpValidation.format === false && <div style={{ marginTop: 4, fontSize: 11, color: '#dc2626' }}>❌ Formato de IP inválido</div>}
                        {!nicIpValidation.checking && nicIpValidation.inSubnet === false && <div style={{ marginTop: 4, fontSize: 11, color: '#dc2626' }}>❌ IP fuera del rango de la subred</div>}
                        {!nicIpValidation.checking && nicIpValidation.occupied === 'assigned' && <div style={{ marginTop: 4, fontSize: 11, color: '#dc2626' }}>❌ IP ya <strong>asignada</strong> en IPAM</div>}
                        {!nicIpValidation.checking && nicIpValidation.occupied === 'conflict' && <div style={{ marginTop: 4, fontSize: 11, color: '#ea580c' }}>⚠️ IP con <strong>conflicto</strong> en IPAM</div>}
                        {!nicIpValidation.checking && nicIpValidation.format === true && nicIpValidation.inSubnet !== false
                          && (nicIpValidation.occupied === null || nicIpValidation.occupied === 'free') && nicDraft?.ip_address && (
                          <div style={{ marginTop: 4, fontSize: 11, color: '#059669' }}>✓ IP disponible y dentro del rango</div>
                        )}
                      </div>
                    )}

                    {/* MAP mode */}
                    {nicIpMode === 'map' && (
                      <div>
                        <div style={{ marginBottom: 6 }}>
                          <label style={{ fontSize: 11, color: '#6b7280', display: 'block', marginBottom: 2 }}>IP seleccionada</label>
                          <div style={{
                            background: nicDraft?.ip_address ? '#f0fdf4' : '#f9fafb',
                            border: `1px solid ${nicDraft?.ip_address ? '#86efac' : '#e5e7eb'}`,
                            borderRadius: 6, padding: '6px 10px', fontFamily: 'monospace',
                            fontSize: 13, color: nicDraft?.ip_address ? '#15803d' : '#9ca3af',
                            fontWeight: nicDraft?.ip_address ? 700 : 400, minHeight: 33,
                          }}>
                            {nicDraft?.ip_address || 'Haz clic en una IP libre del mapa'}
                          </div>
                        </div>
                        {!nicSubnet ? (
                          <div style={{ color: '#9ca3af', fontSize: 12, fontStyle: 'italic' }}>Selecciona primero una subred para ver el mapa.</div>
                        ) : nicMapLoading ? (
                          <div style={{ color: '#9ca3af', fontSize: 12 }}>Cargando mapa de IPs…</div>
                        ) : nicMapData ? (
                          <div>
                            <div style={{ display: 'flex', gap: 12, marginBottom: 5, fontSize: 11, color: '#64748b', flexWrap: 'wrap' }}>
                              <span style={{ color: '#166534', fontWeight: 600 }}>■ {nicMapData.summary?.free ?? 0} libres</span>
                              <span style={{ color: '#991b1b' }}>■ {nicMapData.summary?.assigned ?? 0} asignadas</span>
                              {(nicMapData.summary?.reserved ?? 0) > 0 && <span style={{ color: '#92400e' }}>■ {nicMapData.summary.reserved} reservadas</span>}
                              {nicMapData.truncated && <span style={{ color: '#f59e0b' }}>⚠ mostrando {nicMapData.limit} de {nicMapData.summary?.usable_ips} IPs</span>}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, maxHeight: 190, overflowY: 'auto', padding: 6, border: '1px solid #e5e7eb', borderRadius: 6, background: '#f9fafb' }}>
                              {(nicMapData.generated_ips || []).map(entry => {
                                const isFree = entry.status === 'free' && !entry.is_gateway;
                                const isSelected = nicDraft?.ip_address === entry.ip;
                                let bg, color, cursor, borderColor, title;
                                if (entry.is_gateway) {
                                  bg = '#dbeafe'; color = '#1e40af'; cursor = 'default'; borderColor = '#93c5fd'; title = `GW: ${entry.ip}`;
                                } else if (entry.status === 'assigned') {
                                  bg = '#fee2e2'; color = '#991b1b'; cursor = 'not-allowed'; borderColor = '#fca5a5';
                                  const asgn = entry.assignments?.[0];
                                  title = `Asignada: ${entry.ip}${asgn ? ` → ${asgn.object_name || asgn.object_type}` : ''}`;
                                } else if (entry.status === 'reserved') {
                                  bg = '#fef3c7'; color = '#92400e'; cursor = 'not-allowed'; borderColor = '#fcd34d'; title = `Reservada: ${entry.ip}`;
                                } else {
                                  bg = isSelected ? '#7c3aed' : '#dcfce7'; color = isSelected ? '#fff' : '#166534';
                                  cursor = 'pointer'; borderColor = isSelected ? '#5b21b6' : '#86efac';
                                  title = isSelected ? `Seleccionada: ${entry.ip}` : `Libre: ${entry.ip}`;
                                }
                                return (
                                  <button
                                    key={entry.ip} type="button" title={title}
                                    onClick={() => isFree && setNicDraft(d => ({ ...d, ip_address: isSelected ? '' : entry.ip }))}
                                    style={{
                                      padding: '2px 5px', borderRadius: 3, fontSize: 10, fontFamily: 'monospace',
                                      fontWeight: isSelected ? 700 : 400, background: bg, color, cursor,
                                      border: `1px solid ${borderColor}`, lineHeight: 1.5, whiteSpace: 'nowrap',
                                      transition: 'all 0.08s', pointerEvents: isFree ? 'auto' : 'none',
                                      opacity: isFree || isSelected ? 1 : 0.65,
                                    }}
                                  >
                                    {entry.ip}
                                  </button>
                                );
                              })}
                            </div>
                            <div style={{ marginTop: 4, fontSize: 11, color: '#64748b' }}>🟢 libre &nbsp; 🔴 asignada &nbsp; 🟡 reservada &nbsp; 🔵 gateway</div>
                          </div>
                        ) : (
                          <div style={{ color: '#9ca3af', fontSize: 12 }}>No se pudo cargar el mapa de la subred.</div>
                        )}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={confirmAddNic}
                      disabled={
                        !nicDraft.nic_label?.trim() ||
                        (nicIpMode === 'manual' && (
                          nicIpValidation.format === false ||
                          nicIpValidation.inSubnet === false ||
                          (nicIpValidation.occupied && nicIpValidation.occupied !== 'free' && nicIpValidation.occupied !== 'reserved') ||
                          nicIpValidation.checking
                        ))
                      }
                    >{nicBeingEdited ? 'Guardar cambios' : 'Añadir NIC'}</button>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={cancelNicForm}>Cancelar</button>
                  </div>
                </div>
              ) : !agentBackedNics ? (
                <button
                  onClick={openAddNic}
                  className="vm-dashed-btn"
                >
                  <PlusIcon style={{ width: 14, height: 14 }} /> Añadir tarjeta de red
                </button>
              ) : null}
            </div>
          )}

          {/* ── Tab: Inventario ── */}
          {tab === 'inventory' && (
            <div className="dv-tab-pane">
              <div className="vm-inventory-grid">
                <div className="vm-kpi-grid">
                  {inventoryKpis.map((kpi, idx) => {
                    const IconCmp = kpi.icon;
                    return (
                      <div key={kpi.id} className="vm-kpi-card" style={{ '--kpi-delay': `${idx * 60}ms` }}>
                        <div className="vm-kpi-head">
                          <span className="vm-kpi-icon"><IconCmp style={{ width: 15, height: 15 }} /></span>
                          <span className="vm-kpi-label">{kpi.label}</span>
                        </div>
                        <div className="vm-kpi-value">{kpi.value}</div>
                      </div>
                    );
                  })}
                </div>

                <div className="vm-inventory-card">
                  <div className="vm-inventory-title">IPs activas ({agentInventory.ip_assignments.length})</div>
                  {agentInventory.ip_assignments.length === 0 ? <div className="vm-empty-note">Sin IPs activas asociadas.</div> : (
                    <div className="vm-chip-list">
                      {agentInventory.ip_assignments.map((entry) => (
                        <span key={entry.id} className={`vm-chip ${entry.is_primary ? 'is-primary' : ''}`}>
                          {entry.ip}{entry.interface_ref ? ` · ${entry.interface_ref}` : ''}{entry.is_primary ? ' · primary' : ''}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="vm-inventory-card">
                  <div className="vm-inventory-title">Roles de Windows ({agentInventory.windows_roles.filter(r => r.feature_type === 'Role').length})</div>
                  {agentInventory.windows_roles.filter(r => r.feature_type === 'Role').length === 0 ? <div className="vm-empty-note">No se han detectado roles.</div> : (
                    <div className="vm-inventory-list vm-inventory-list-spaced">
                      {agentInventory.windows_roles.filter(r => r.feature_type === 'Role').map((entry, index) => (
                        <div key={`${entry.name || 'role'}-${index}`} className="vm-row-text">
                          <strong>{entry.display_name || entry.name}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="vm-inventory-title vm-mt-md">Características de Windows ({agentInventory.windows_roles.filter(r => r.feature_type === 'Feature').length})</div>
                  {agentInventory.windows_roles.filter(r => r.feature_type === 'Feature').length === 0 ? <div className="vm-empty-note">No se han detectado características.</div> : (
                    <div className="vm-inventory-list">
                      {agentInventory.windows_roles.filter(r => r.feature_type === 'Feature').map((entry, index) => (
                        <div key={`${entry.name || 'feature'}-${index}`} className="vm-row-text">
                          <strong>{entry.display_name || entry.name}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="vm-inventory-card">
                  <SoftwareInventoryPanel
                    companyId={companyId}
                    hostCiType="virt_vm"
                    hostCiId={vm?.id}
                    fallbackEntries={Array.isArray(agentInventory.installed_software) ? agentInventory.installed_software : []}
                  />
                </div>

                <div className="vm-inventory-card">
                  <div className="vm-inventory-title">Servicios ({agentInventory.services.length})</div>
                  {agentInventory.services.length === 0 ? <div className="vm-empty-note">Todavía no hay snapshot de monitoring con servicios.</div> : (
                    <>
                      <div className="vm-table-toolbar">
                        <input
                          className="dv-input vm-input-compact"
                          placeholder="Buscar servicio, estado o tipo de inicio…"
                          value={servicesSearch}
                          onChange={(e) => setServicesSearch(e.target.value)}
                        />
                      </div>
                      <div className="vm-table-scroll">
                        <table className="vm-compact-table">
                          <thead>
                            <tr>
                              <th role="button" onClick={() => _toggleSort(setServicesSort, 'display_name')}>
                                Servicio {servicesSort.key === 'display_name' ? (servicesSort.dir === 'asc' ? '↑' : '↓') : ''}
                              </th>
                              <th role="button" onClick={() => _toggleSort(setServicesSort, 'status')}>
                                Estado {servicesSort.key === 'status' ? (servicesSort.dir === 'asc' ? '↑' : '↓') : ''}
                              </th>
                              <th role="button" onClick={() => _toggleSort(setServicesSort, 'start_type')}>
                                Inicio {servicesSort.key === 'start_type' ? (servicesSort.dir === 'asc' ? '↑' : '↓') : ''}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {serviceRows.map((row) => (
                              <tr key={row.id}>
                                <td>{row.display_name}</td>
                                <td className="vm-cell-mono vm-cell-muted">{row.status}</td>
                                <td className="vm-cell-subtle">{row.start_type}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {serviceRows.length === 0 && <div className="vm-empty-note">No hay resultados para la búsqueda actual.</div>}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Monitorización ── */}
          {tab === 'monitoring' && (
            <div className="dv-tab-pane">
              {vm?.id ? (
                <VirtVMMonitoringPanel serverId={vm.id} companyId={companyId} />
              ) : (
                <div className="text-muted text-center p-4">
                  Guarde la máquina virtual primero para ver monitorización.
                </div>
              )}
            </div>
          )}

          {/* ── Tab: Agente ── */}
          {tab === 'agente' && (
            <div className="dv-tab-pane">
              {vm?.id && (
                <RemoteActions
                  companyId={companyId}
                  ciType="virt_vm"
                  ciId={vm.id}
                />
              )}
              <div style={{ marginTop: 16 }}>
                <AgentActivityPanel
                  companyId={companyId}
                  ciType="virt_vm"
                  ciId={vm?.id}
                  agentId={agentTelemetry.agent_id}
                  agentVersion={agentTelemetry.agent_version}
                  agentStatus={agentTelemetry.agent_status || 'unknown'}
                  lastSeenAt={agentTelemetry.last_seen_at}
                  latestVersion={latestAgentVersion || agentUpdateStatus?.target_version || null}
                />
              </div>
            </div>
          )}

          {/* ── Tab: Delegación ── */}
          {tab === 'deleg' && !isDelegatedView && (
            <div className="dv-tab-pane">
              <div className="dv-field-grid">
                <div className="dv-field-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="dv-label">
                    <BuildingOfficeIcon style={{ width: 14, height: 14, display: 'inline', marginRight: 4 }} />
                    Empresa delegada (acceso externo)
                  </label>
                  <select
                    className="dv-select"
                    value={form.delegated_company_id}
                    onChange={e => sf('delegated_company_id', e.target.value)}
                    disabled={!isOwnerView}
                  >
                    <option value="">— No delegada —</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <p style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                    Al delegar esta VM, la empresa seleccionada podrá verla en su inventario bajo el mismo ID.
                  </p>
                  {!isOwnerView && (
                    <p style={{ marginTop: 6, fontSize: 12, color: '#b45309' }}>
                      Solo la empresa propietaria puede modificar la delegación.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          </fieldset>
          <FormDirtyDock
            show={isCards && isDirty}
            saving={saving}
            onDiscard={handleCancelEdit}
            onSave={handleSave}
          />
          </FormCardsShell>
        </div>
        </fieldset>
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="virt_vm"
          ciId={vm?.id}
          tipoLegible="la máquina virtual"
        />
      </div>
    







﻿
      {/* ── Sync Modal ── */}
      {showSyncModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.55)' }} onClick={closeSyncModal} />
          <div style={{
            position: 'relative', width: 480, maxWidth: '92vw', background: 'var(--slate-100)',
            border: '1px solid var(--slate-200)', borderRadius: 12, boxShadow: '0 25px 50px rgba(0,0,0,.25)',
            padding: '1.5rem', color: 'var(--text-primary)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h5 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>
                📡 {syncMode === 'update_agent' ? 'Actualización de agente' : 'Sincronización de Máquina Virtual'}
              </h5>
              <button type="button" onClick={closeSyncModal}
                style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 20 }}>✕</button>
            </div>

            {/* Server info */}
            <div style={{ background: 'var(--slate-200)', borderRadius: 8, padding: '0.65rem 0.9rem', marginBottom: 16, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{vm?.name}</span>
              {agentTelemetry?.agent_id && <span style={{ marginLeft: 8, fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}>· {agentTelemetry.agent_id.slice(0,12)}…</span>}
              <span style={{ marginLeft: 8 }}>· Estado: <strong style={{ color: 'var(--text-primary)' }}>{agentTelemetry.agent_status || 'unknown'}</strong></span>
            </div>

            {/* Steps timeline */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
              {[
                { key: 'enqueueing', label: 'Enviando comando a la VM…', icon: '📤' },
                { key: 'pending', label: `Comando en cola — esperando que el agente lo recoja (${syncElapsed}s)`, icon: '⏳' },
                {
                  key: 'running',
                  label: syncMode === 'update_agent'
                    ? `Agente aplicando actualización en background… (${syncElapsed}s)`
                    : `Agente ejecutando rescan completo de la VM… (${syncElapsed}s)`,
                  icon: '🔄',
                },
                { key: 'done', label: 'Sincronización completada', icon: '✅' },
                { key: 'error', label: syncError || 'Error durante la sincronización', icon: '❌' },
              ].map((step) => {
                const stages = ['enqueueing', 'pending', 'running', 'done', 'error'];
                const currentIdx = stages.indexOf(syncStep);
                const stepIdx = stages.indexOf(step.key);
                const isError = syncStep === 'error';
                const isActive = step.key === syncStep;
                const isDone = !isError && stepIdx < currentIdx;
                if (step.key === 'error' && !isError) return null;
                if (isError && stepIdx > 0 && stepIdx < stages.indexOf('error') && stepIdx > currentIdx) return null;

                return (
                  <div key={step.key} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '0.5rem 0.7rem', borderRadius: 8,
                    background: isActive ? 'rgba(99,102,241,.12)' : 'transparent',
                    opacity: isDone ? 0.5 : isActive ? 1 : 0.3,
                    transition: 'all 0.3s',
                  }}>
                    <span style={{ fontSize: 18, width: 24, textAlign: 'center' }}>
                      {isDone ? '✓' : step.icon}
                    </span>
                    <span style={{
                      fontSize: '0.85rem', fontWeight: isActive ? 600 : 400,
                      color: isActive ? 'var(--text-primary)' : 'var(--text-muted)',
                    }}>{step.label}</span>
                    {isActive && step.key !== 'done' && step.key !== 'error' && (
                      <span className="dv-spinner" style={{ width: 14, height: 14, marginLeft: 'auto', border: '2px solid var(--text-primary)', borderRightColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Result details */}
            {syncStep === 'done' && syncResult && (
              <div style={{
                background: 'rgba(34,197,94,.08)', border: '1px solid rgba(34,197,94,.25)',
                borderRadius: 8, padding: '0.65rem 0.9rem', fontSize: '0.82rem', color: '#16a34a', marginBottom: 12,
              }}>
                {syncResult.message || (syncMode === 'update_agent'
                  ? 'El comando de actualización ha sido despachado.'
                  : 'El agente ha completado el escaneo. Los datos se actualizarán automáticamente.')}
              </div>
            )}

            {syncStep === 'error' && (
              <div style={{
                background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.25)',
                borderRadius: 8, padding: '0.65rem 0.9rem', fontSize: '0.82rem', color: '#dc2626', marginBottom: 12,
              }}>
                {syncError}
              </div>
            )}

            {!!syncNotice && syncStep !== 'error' && (
              <div style={{
                background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.25)',
                borderRadius: 8, padding: '0.65rem 0.9rem', fontSize: '0.82rem', color: '#d97706', marginBottom: 12,
              }}>
                {syncNotice}
              </div>
            )}

            {/* Agent offline warning */}
            {agentTelemetry?.agent_status === 'offline' && (syncStep === 'pending' || syncStep === 'running' || syncStep === 'enqueueing') && (
              <div style={{
                background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.25)',
                borderRadius: 8, padding: '0.65rem 0.9rem', fontSize: '0.82rem', color: '#d97706', marginBottom: 12,
              }}>
                ⚠️ El agente está <strong>offline</strong>. El comando quedará pendiente hasta que vuelva a conectar.
              </div>
            )}

            {/* Close button */}
            <div style={{ textAlign: 'right' }}>
              <button type="button" onClick={closeSyncModal} style={{
                background: syncStep === 'done' ? 'linear-gradient(135deg, #6366f1, #4f46e5)' : 'var(--slate-200)',
                color: syncStep === 'done' ? '#fff' : 'var(--text-primary)', border: 'none', borderRadius: 6,
                padding: '0.4rem 1.2rem', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
              }}>
                {syncStep === 'done' || syncStep === 'error' ? 'Cerrar' : 'Cancelar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAgentModal && (
        <div className="vm-agent-modal-backdrop" onClick={() => setShowAgentModal(false)}>
          <div className="vm-agent-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vm-agent-modal-header">
              <h4>Estado del agente CMDB</h4>
              <button type="button" className="vm-icon-btn" onClick={() => setShowAgentModal(false)}>
                ✕
              </button>
            </div>

            <div className="vm-agent-modal-grid">
              <div className="vm-agent-modal-card">
                <div className="vm-agent-modal-label">Estado de conexión</div>
                <span className="vm-badge" style={{ background: agentInfo.bg, color: agentInfo.color }}>{agentInfo.label}</span>
              </div>
              <div className="vm-agent-modal-card">
                <div className="vm-agent-modal-label">Estado guest tools</div>
                <div className="vm-agent-modal-main" style={{ color: guestInfo.color }}>{guestInfo.label}</div>
                <div className="vm-agent-modal-help">{guestHelpText}</div>
              </div>
              <div className="vm-agent-modal-card">
                <div className="vm-agent-modal-label">Última actividad</div>
                <div className="vm-agent-modal-main">{agentTelemetry.last_seen_at ? new Date(agentTelemetry.last_seen_at).toLocaleString() : 'Sin dato'}</div>
              </div>
              <div className="vm-agent-modal-card">
                <div className="vm-agent-modal-label">Identificador</div>
                <div className="vm-agent-modal-main vm-meta-mono">{agentTelemetry.agent_id || 'No enlazado'}</div>
              </div>
              <div className="vm-agent-modal-card">
                <div className="vm-agent-modal-label">Versión agente</div>
                <div className="vm-agent-modal-main vm-meta-mono">{agentTelemetry.agent_version || 'No reportada'}</div>
              </div>
            </div>

            {agentUpdate && (
              <div
                style={{
                  marginTop: 12,
                  padding: '0.65rem 0.85rem',
                  borderRadius: 10,
                  border: '1px solid var(--border)',
                  fontSize: '0.8rem',
                  display: 'grid',
                  gap: 2,
                }}
              >
                <span style={{ color: agentUpdate.color, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <i style={{ width: 8, height: 8, borderRadius: 999, background: agentUpdate.color, display: 'inline-block' }} />
                  {agentUpdate.label}
                </span>
                <span style={{ color: 'var(--text-muted)' }}>{agentUpdate.title}</span>
              </div>
            )}

            {isEditing && agentTelemetry.agent_id && (
              <div className="vm-agent-modal-actions">
                <button
                  type="button"
                  className="dv-btn dv-btn-secondary"
                  onClick={handleTriggerAgentUpdate}
                  disabled={saving}
                  title="Encola comando remoto update_agent"
                >
                  {saving ? 'Encolando…' : 'Actualizar agente'}
                </button>
                <button type="button" className="dv-btn dv-btn-primary" onClick={() => setShowAgentModal(false)}>
                  Cerrar
                </button>
              </div>
            )}
          </div>
        </div>
      )}

</div>
  );
}

